import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket, { initSocket } from '../services/socket';
import AdminMinigameTab from './AdminMinigameTab';
import CustomMarketEvents from './CustomMarketEvents'; // Import the new component
import AdminMinigameTriggerTab from './AdminMinigameTriggerTab';
import AdminUserManagementTab from './AdminUserManagementTab';
import AdminQRCodeTab from './AdminQRCodeTab';
import AdminGuestRegistration from './AdminGuestRegistration';
import AdminRecentRegistrations from './AdminRecentRegistrations';
import AdminOverrides from './AdminOverrides';
import AdminLEDControl from './AdminLEDControl';
import AdminBattleship from './AdminBattleship';
import AdminEventTester from './AdminEventTester';
import AdminObservability from './AdminObservability';
import AdminTimeline from './AdminTimeline';
import AdminBriefcaseBlitz from './AdminBriefcaseBlitz';
import AdminMinigolf from './AdminMinigolf';
import AdminMoneyTunnel from './AdminMoneyTunnel';
import AdminTeams from './AdminTeams';
import AdminConcentrationWatch from './AdminConcentrationWatch';
import CeoManager from './CeoManager';
import '../styles/AdminPanel.css';
import '../styles/AdminExtensions.css';

// Helper to get admin token
const getAdminToken = () => localStorage.getItem('adminKey') || '';

// Create axios instance with admin auth header
const adminApi = axios.create({
  baseURL: API_BASE_URL,
});

// Add admin token to all requests
adminApi.interceptors.request.use((config) => {
  config.headers['x-admin-token'] = getAdminToken();
  return config;
});

const AdminPanel = () => {
  const navigate = useNavigate();
  const { tab, subtab } = useParams();

  // Existing state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });

  // Tab state from URL params (flat structure - no sub-tabs)
  const activeTab = tab || 'dashboard';

  // Simple navigation helper for flat tabs
  const navigateToTab = (newTab) => {
    navigate(`/admin/${newTab}`);
  };
  const [marketSettings, setMarketSettings] = useState({
    pressureImpactFactor: 0.5,
    updateInterval: 5000,
    eventProbability: 0.3
  });
  const [tradingPressure, setTradingPressure] = useState({});
  const [tradingActivity, setTradingActivity] = useState([]);
  const [stockPrices, setStockPrices] = useState([]);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  
  // Reset functionality state
  const [adminKey, setAdminKey] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState({ message: '', type: '' });
  
  // New admin features state
  const [marketState, setMarketState] = useState({ isOpen: false, tradingEnabled: true });
  const [dashboardData, setDashboardData] = useState(null);
  const [systemHealth, setSystemHealth] = useState({
    database: 'checking',
    server: 'checking',
    websocket: 'checking',
    lastUpdate: Date.now()
  });
  const [realtimeStats, setRealtimeStats] = useState({
    activeTrades: 0,
    onlineUsers: 0,
    tradesPerMinute: 0,
    avgResponseTime: 0
  });
  const [qrGenerator, setQrGenerator] = useState({
    type: 'bonus',
    amount: '',
    reason: '',
    generatedQR: null
  });
  const [bigGameData, setBigGameData] = useState({
    gameName: '',
    announcement: '',
    pauseTrading: false
  });
  const [macroControlState, setMacroControlState] = useState({
    eventsPaused: false,
    tradingPaused: false,
    pricesPaused: false,
    marketPaused: false,
    isTradingAllowed: true,
    areEventsAllowed: true,
    arePricesUpdating: true
  });

  // Party control state
  const [partyState, setPartyState] = useState({
    phase: 'not_started', // 'not_started', 'premarket', 'orientation', 'trading', 'macro_game', 'ended'
    premarketMinutes: 60,
    broadcastUrl: '',
    macroGameName: 'Portfolio Warfare',
    macroGameSeconds: 300,
    tradingBlockMinutes: 30,
    tradingBlockNumber: 1, // Which trading block we're in (1-4)
    completedGames: [] // Track which macro games have been completed
  });
  const [isStartingParty, setIsStartingParty] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Live countdown state
  const [liveCountdown, setLiveCountdown] = useState({
    active: false,
    type: null, // 'market-open', 'market-close', 'macro-game', 'premarket'
    secondsRemaining: 0,
    totalSeconds: 0,
    label: '',
    endTime: null
  });

  // Initialize socket on mount (admin panel needs its own connection)
  useEffect(() => {
    if (!socket.get()) {
      initSocket('admin-panel');
    }

    // Listen for socket connection state changes
    const handleConnect = () => {
      setSystemHealth(prev => ({ ...prev, websocket: 'healthy', lastUpdate: Date.now() }));
    };
    const handleDisconnect = () => {
      setSystemHealth(prev => ({ ...prev, websocket: 'disconnected', lastUpdate: Date.now() }));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Check initial state after a brief delay for connection
    setTimeout(() => {
      const s = socket.get();
      if (s?.connected) {
        setSystemHealth(prev => ({ ...prev, websocket: 'healthy', lastUpdate: Date.now() }));
      }
    }, 500);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  // Sync party state from server on mount (server is source of truth)
  useEffect(() => {
    const syncPartyState = async () => {
      try {
        // Fetch full party state from server (single source of truth)
        const response = await fetch(`${API_BASE_URL}/api/party-state`);
        const data = await response.json();

        if (data.success) {
          // Update party state from server
          setPartyState(prev => ({
            ...prev,
            phase: data.partyState.phase || 'not_started',
            tradingBlockNumber: data.partyState.tradingBlockNumber || 1,
            completedGames: data.partyState.completedGames || [],
            macroGameName: data.partyState.currentMacroGame || prev.macroGameName
          }));

          // Update market state from server
          if (data.marketState) {
            setMarketState(data.marketState);
          }

          // Update broadcast URL if set
          if (data.broadcastUrl) {
            setPartyState(prev => ({ ...prev, broadcastUrl: data.broadcastUrl }));
          }
        }

        // Also fetch countdown state
        let hasActiveCountdown = false;
        const countdownRes = await fetch(`${API_BASE_URL}/api/market-countdown`);
        const countdownData = await countdownRes.json();
        if (countdownData.active) {
          hasActiveCountdown = true;
          setLiveCountdown({
            active: true,
            type: countdownData.countdownType || 'market-open',
            secondsRemaining: countdownData.secondsRemaining,
            totalSeconds: countdownData.totalSeconds,
            label: countdownData.countdownType === 'market-open' ? 'Opening Bell' :
                   countdownData.countdownType === 'market-close' ? 'Market Closes' :
                   countdownData.countdownType === 'macro-game' ? countdownData.gameName || 'Macro Game' : 'Countdown',
            endTime: Date.now() + (countdownData.secondsRemaining * 1000)
          });
        }

        // Also check macro game countdown if no market countdown is active
        if (!hasActiveCountdown) {
          const macroRes = await fetch(`${API_BASE_URL}/api/macro-countdown`);
          const macroData = await macroRes.json();
          if (macroData.active) {
            hasActiveCountdown = true;
            setLiveCountdown({
              active: true,
              type: 'macro-game',
              secondsRemaining: macroData.secondsRemaining,
              totalSeconds: macroData.totalSeconds,
              label: macroData.gameName || 'Macro Game',
              endTime: Date.now() + (macroData.secondsRemaining * 1000)
            });
          }
        }

        // Also check premarket countdown if no other countdown is active
        if (!hasActiveCountdown) {
          const premarketRes = await fetch(`${API_BASE_URL}/api/premarket-countdown`);
          const premarketData = await premarketRes.json();
          if (premarketData.active) {
            let label = 'Pre-Market';
            if (premarketData.phase === 'orientation') {
              label = 'Orientation';
            } else if (premarketData.phase === 'countdown') {
              label = 'Opening Bell';
            }
            setLiveCountdown({
              active: true,
              type: 'premarket',
              secondsRemaining: premarketData.secondsRemaining,
              totalSeconds: premarketData.totalSeconds,
              label: label,
              endTime: Date.now() + (premarketData.secondsRemaining * 1000)
            });
          }
        }
      } catch (error) {
        console.log('Could not sync party state from server:', error);
      }
    };

    syncPartyState();
  }, []);

  // Load data based on active tab (flat structure)
  useEffect(() => {
    // Dashboard tab
    if (activeTab === 'dashboard') {
      fetchDashboardData();
      checkSystemHealth();
      const interval = setInterval(() => {
        fetchDashboardData();
        checkSystemHealth();
      }, 5000);
      return () => clearInterval(interval);
    }

    // Trades tab
    if (activeTab === 'trades') {
      fetchTradingPressure();
      fetchStockPrices();
      startListeningToTrades();
      const interval = setInterval(() => {
        fetchTradingPressure();
        fetchStockPrices();
      }, 3000);
      return () => clearInterval(interval);
    }

    // Market tab
    if (activeTab === 'market') {
      fetchStockPrices();
    }

    // Bell tab
    if (activeTab === 'bell') {
      fetchMarketStatus();
    }

    // Games tab
    if (activeTab === 'games' || activeTab === 'games-macro') {
      fetchMarketStatus();
      fetchMacroControlState();
      const interval = setInterval(() => {
        fetchMacroControlState();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);
  
  // Socket listeners for real-time updates
  useEffect(() => {
    const handleTrade = (tradeData) => {
      console.log('Admin panel received trade:', tradeData);
      setTradingActivity(prev => [tradeData, ...prev].slice(0, 100));
    };
    
    const handlePressureData = (data) => {
      setTradingPressure(data);
    };
    
    const handleStockUpdates = (updates) => {
      setStockPrices(prev => {
        // Merge updates with existing data
        const updatedStocks = [...prev];
        
        updates.forEach(update => {
          const index = updatedStocks.findIndex(s => s.id === update.id);
          if (index !== -1) {
            updatedStocks[index] = {...updatedStocks[index], ...update};
          } else {
            updatedStocks.push(update);
          }
        });
        
        return updatedStocks;
      });
    };
    
    // Market event listeners for admin panel
    const handleMarketBell = (data) => {
      console.log('Admin panel received market-bell event:', data);
      setMarketState(prev => ({
        ...prev,
        isOpen: data.isOpen,
        tradingEnabled: data.isOpen,
        openedAt: data.timestamp
      }));
      // Update party phase
      if (data.type === 'OPENING_BELL') {
        setPartyState(prev => ({ ...prev, phase: 'trading' }));
      } else if (data.type === 'CLOSING_BELL') {
        setPartyState(prev => ({ ...prev, phase: 'ended' }));
      }
      setMessage({
        text: `Market Bell: ${data.message}`,
        type: 'success'
      });
      // Auto-clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const handlePremarketStart = (data) => {
      console.log('Admin panel received premarket-start:', data);
      setPartyState(prev => ({ ...prev, phase: 'premarket' }));
      // Update countdown display
      const totalSeconds = data.minutes ? data.minutes * 60 : Math.floor((data.duration || 3600000) / 1000);
      setLiveCountdown({
        active: true,
        type: 'premarket',
        secondsRemaining: totalSeconds,
        totalSeconds: totalSeconds,
        label: 'Opening Bell',
        endTime: data.endTime
      });
    };

    const handleMacroGameCountdown = (data) => {
      console.log('Admin panel received macro-game-countdown:', data);
      setPartyState(prev => ({ ...prev, phase: 'macro_game', macroGameName: data.gameName || prev.macroGameName }));
    };

    const handleMacroGameCountdownEnd = () => {
      console.log('Admin panel received macro-game-countdown-end');
      // Check if market is open, if so go back to trading
      setPartyState(prev => {
        if (prev.phase === 'macro_game') {
          // Add the completed game and increment trading block
          const newCompletedGames = [...prev.completedGames];
          if (!newCompletedGames.includes(prev.macroGameName)) {
            newCompletedGames.push(prev.macroGameName);
          }
          return {
            ...prev,
            phase: 'trading',
            completedGames: newCompletedGames,
            tradingBlockNumber: Math.min(prev.tradingBlockNumber + 1, 4)
          };
        }
        return prev;
      });
    };
    
    const handleBigGameAnnouncement = (data) => {
      console.log('Admin panel received big-game-announcement:', data);
      setMarketState(prev => ({ ...prev, tradingEnabled: !data.tradingPaused }));
      setMessage({
        text: `Big Game Announced: ${data.gameName}`,
        type: 'success'
      });
      // Auto-clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };
    
    const handleTradingPaused = (data) => {
      console.log('Admin panel received trading-paused:', data);
      setMarketState(prev => ({ ...prev, tradingEnabled: false }));
      setMessage({
        text: data.message,
        type: 'success'
      });
      // Auto-clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };
    
    const handleTradingResumed = (data) => {
      console.log('Admin panel received trading-resumed:', data);
      setMarketState(prev => ({ ...prev, tradingEnabled: true }));
      setMessage({
        text: data.message,
        type: 'success'
      });
      // Auto-clear message after 5 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    // Listen for party state updates from server (source of truth)
    const handlePartyStateUpdate = (data) => {
      console.log('Admin panel received party-state-update:', data);
      if (data.partyState) {
        setPartyState(prev => ({
          ...prev,
          phase: data.partyState.phase || prev.phase,
          tradingBlockNumber: data.partyState.tradingBlockNumber || prev.tradingBlockNumber,
          completedGames: data.partyState.completedGames || prev.completedGames,
          macroGameName: data.partyState.currentMacroGame || prev.macroGameName
        }));
      }
    };

    const handleAdminBalanceUpdate = (data) => {
      console.log('Admin panel received admin-balance-update:', data);
      setMessage({
        text: `Balance updated: ${data.amount >= 0 ? '+' : ''}$${data.amount} - ${data.reason}`,
        type: 'success'
      });
      // Auto-clear message after 3 seconds
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    };

    // Countdown event handlers
    const handleCountdownStart = (data) => {
      console.log('Admin panel received countdown-start:', data);
      setLiveCountdown({
        active: true,
        type: data.countdownType || 'market-open',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.countdownType === 'market-open' ? 'Opening Bell' :
               data.countdownType === 'market-close' ? 'Market Closes' :
               data.countdownType === 'macro-game' ? data.gameName || 'Macro Game' : 'Countdown',
        endTime: data.endTime
      });
    };

    const handleCountdownUpdate = (data) => {
      console.log('Admin panel received countdown-update:', data);
      setLiveCountdown(prev => ({
        ...prev,
        secondsRemaining: data.seconds,
        endTime: data.endTime
      }));
    };

    const handleCountdownCancelled = () => {
      console.log('Admin panel received countdown-cancelled');
      setLiveCountdown({
        active: false,
        type: null,
        secondsRemaining: 0,
        totalSeconds: 0,
        label: '',
        endTime: null
      });
    };

    const handleMacroGameCountdownForDisplay = (data) => {
      console.log('Admin panel received macro-game-countdown for display:', data);
      setLiveCountdown({
        active: true,
        type: 'macro-game',
        secondsRemaining: data.seconds,
        totalSeconds: data.totalSeconds || data.seconds,
        label: data.gameName || 'Macro Game',
        endTime: data.endTime
      });
    };

    // Register listeners
    socket.on('trade-executed', handleTrade);
    socket.on('position-closed', handleTrade);
    socket.on('trading-pressure-data', handlePressureData);
    socket.on('stock-updates', handleStockUpdates);
    socket.on('market-bell', handleMarketBell);
    socket.on('big-game-announcement', handleBigGameAnnouncement);
    socket.on('trading-paused', handleTradingPaused);
    socket.on('trading-resumed', handleTradingResumed);
    socket.on('admin-balance-update', handleAdminBalanceUpdate);
    socket.on('premarket-start', handlePremarketStart);
    socket.on('macro-game-countdown', handleMacroGameCountdown);
    socket.on('macro-game-countdown-end', handleMacroGameCountdownEnd);
    socket.on('party-state-update', handlePartyStateUpdate);
    // Countdown display listeners
    socket.on('countdown-start', handleCountdownStart);
    socket.on('countdown-update', handleCountdownUpdate);
    socket.on('countdown-cancelled', handleCountdownCancelled);
    socket.on('macro-game-countdown', handleMacroGameCountdownForDisplay);

    // Request initial data
    if (activeTab === 'market' || activeTab === 'trades' || activeTab === 'custom-events') {
      socket.emit('get-trading-pressure');
    }

    return () => {
      // Clean up listeners
      socket.off('trade-executed', handleTrade);
      socket.off('position-closed', handleTrade);
      socket.off('trading-pressure-data', handlePressureData);
      socket.off('stock-updates', handleStockUpdates);
      socket.off('market-bell', handleMarketBell);
      socket.off('big-game-announcement', handleBigGameAnnouncement);
      socket.off('trading-paused', handleTradingPaused);
      socket.off('trading-resumed', handleTradingResumed);
      socket.off('admin-balance-update', handleAdminBalanceUpdate);
      socket.off('premarket-start', handlePremarketStart);
      socket.off('macro-game-countdown', handleMacroGameCountdown);
      socket.off('macro-game-countdown-end', handleMacroGameCountdownEnd);
      socket.off('party-state-update', handlePartyStateUpdate);
      socket.off('countdown-start', handleCountdownStart);
      socket.off('countdown-update', handleCountdownUpdate);
      socket.off('countdown-cancelled', handleCountdownCancelled);
    };
  }, [activeTab]);

  // Countdown tick effect
  useEffect(() => {
    if (!liveCountdown.active || !liveCountdown.endTime) return;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((liveCountdown.endTime - now) / 1000));

      if (remaining <= 0) {
        setLiveCountdown(prev => ({
          ...prev,
          active: false,
          secondsRemaining: 0
        }));
      } else {
        setLiveCountdown(prev => ({
          ...prev,
          secondsRemaining: remaining
        }));
      }
    };

    tick(); // Initial tick
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [liveCountdown.active, liveCountdown.endTime]);

  // Format seconds to MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get next phase info based on current phase
  const getNextPhaseInfo = () => {
    const phase = partyState.phase;
    const block = partyState.tradingBlockNumber;

    const phaseFlow = {
      'not_started': { next: 'Registration + Premarket', action: 'Start Party' },
      'registration': { next: 'Orientation Video', action: 'Start Orientation' },
      'premarket': { next: 'Orientation Video', action: 'Start Orientation' },
      'orientation': { next: 'Opening Bell Countdown', action: 'Start Countdown' },
      'trading': {
        1: { next: 'Portfolio Warfare', action: 'Start Portfolio Warfare' },
        2: { next: 'Dead Cat Bounce', action: 'Start Dead Cat Bounce' },
        3: { next: 'A Brief Case', action: 'Start A Brief Case' },
        4: { next: 'Market Close', action: 'Close Market' }
      }[block] || { next: 'Macro Game', action: 'Start Game' },
      'macro_game': { next: ['European Session', 'Canadian Session', 'Closing Bell', 'Results'][block - 1] || 'Trading', action: block >= 4 ? 'End Party' : 'Resume Trading' },
      'ended': { next: 'Party Complete!', action: null }
    };

    return phaseFlow[phase] || { next: 'Unknown', action: null };
  };

  // fetchJackets removed - stretch goal for future parties

  // Fetch trading pressure data
  const fetchTradingPressure = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await adminApi.get('/api/admin/trading-pressure');
      setTradingPressure(response.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching trading pressure:', err);
      setError('Failed to load trading pressure data');
      setLoading(false);
    }
  };
  
  // Fetch stock prices
  const fetchStockPrices = async () => {
    try {
      const response = await adminApi.get('/api/stocks');
      setStockPrices(response.data);
    } catch (err) {
      console.error('Error fetching stock prices:', err);
    }
  };
  
  // Start listening to trading activity
  const startListeningToTrades = () => {
    // Initial state if needed - actual trades will come from socket
    setTradingActivity([]);
  };
  
  // sendTestCommand removed - jacket feature is stretch goal for future parties

  // Update market engine settings
  const updateMarketSettings = async (e) => {
    e.preventDefault();
    
    try {
      setIsUpdatingSettings(true);
      setError('');
      
      const response = await adminApi.post('/api/admin/market-settings', marketSettings);
      
      setMessage({
        text: 'Market settings updated successfully',
        type: 'success'
      });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (err) {
      console.error('Error updating market settings:', err);
      setError(err.response?.data?.message || 'Failed to update market settings');
    } finally {
      setIsUpdatingSettings(false);
    }
  };
  
  // Trigger a market event
  const triggerMarketEvent = async (type) => {
    try {
      setError('');
      
      // Create event data based on type
      let eventData = {};
      
      switch(type) {
        case 'CRASH':
          eventData = {
            type: 'MARKET_CRASH',
            description: 'Manual Market Crash triggered by admin! All stocks down 20%',
            multiplier: 0.8,
            affectedStocks: 'ALL'
          };
          break;
        case 'BULL':
          eventData = {
            type: 'BULL_RUN',
            description: 'Manual Bull Run triggered by admin! All stocks up 20%',
            multiplier: 1.2,
            affectedStocks: 'ALL'
          };
          break;
        default:
          throw new Error('Unknown event type');
      }
      
      const response = await adminApi.post('/api/admin/trigger-event', eventData);
      
      setMessage({
        text: `Market event "${eventData.description}" triggered successfully`,
        type: 'success'
      });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } catch (err) {
      console.error('Error triggering market event:', err);
      setError(err.response?.data?.message || 'Failed to trigger market event');
    }
  };
  
  // selectJacket removed - stretch goal for future parties

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };
  
  // Calculate performance class (for coloring)
  const getPerformanceClass = (initialBalance, currentBalance) => {
    const performance = ((currentBalance - initialBalance) / initialBalance) * 100;
    
    if (performance > 10) return 'high-gain';
    if (performance > 0) return 'gain';
    if (performance > -10) return 'loss';
    return 'high-loss';
  };
  
  // Calculate performance percentage
  const calculatePerformance = (initialBalance, currentBalance) => {
    const performance = ((currentBalance - initialBalance) / initialBalance) * 100;
    return performance.toFixed(2);
  };
  
  // Render market pressure data
  const renderTradingPressure = () => {
    const stocks = Object.keys(tradingPressure);
    
    if (stocks.length === 0) {
      return <p>No trading pressure data available</p>;
    }
    
    // Find matching stock info for each pressure entry
    const pressureWithInfo = stocks.map(stockId => {
      const pressure = tradingPressure[stockId];
      const stockInfo = stockPrices.find(s => s.id === stockId) || { 
        ticker: 'Unknown',
        currentPrice: 0
      };
      
      return {
        stockId,
        ticker: stockInfo.ticker,
        currentPrice: stockInfo.currentPrice,
        ...pressure
      };
    });
    
    // Sort by net pressure (absolute value) to show most active first
    const sortedPressure = [...pressureWithInfo].sort((a, b) => 
      Math.abs(b.netPressure || 0) - Math.abs(a.netPressure || 0)
    );
    
    return (
      <table className="trading-pressure-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Price</th>
            <th>Net Pressure</th>
            <th>Buy Pressure</th>
            <th>Sell Pressure</th>
            <th>Volume</th>
            <th>Last Trade</th>
          </tr>
        </thead>
        <tbody>
          {sortedPressure.map(stock => (
            <tr key={stock.stockId}>
              <td>{stock.ticker}</td>
              <td>${stock.currentPrice?.toFixed(2) || '0.00'}</td>
              <td className={stock.netPressure > 0 ? 'positive' : stock.netPressure < 0 ? 'negative' : ''}>
                {stock.netPressure?.toFixed(2) || '0.00'}
              </td>
              <td>{stock.buyPressure?.toFixed(2) || '0.00'}</td>
              <td>{stock.sellPressure?.toFixed(2) || '0.00'}</td>
              <td>{stock.tradeVolume || 0}</td>
              <td>{stock.lastTradeTime ? new Date(stock.lastTradeTime).toLocaleTimeString() : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };
  
  // Render trading activity
  const renderTradingActivity = () => {
    if (tradingActivity.length === 0) {
      return <p>No recent trading activity</p>;
    }
    
    return (
      <table className="trading-activity-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Stock</th>
            <th>Action</th>
            <th>Shares</th>
            <th>Price</th>
            <th>Value</th>
            <th>P&L</th>
            <th>User</th>
          </tr>
        </thead>
        <tbody>
          {tradingActivity.map((trade, index) => {
            const stockInfo = stockPrices.find(s => s.id === trade.stockId) || { ticker: 'Unknown' };
            const tradeValue = (trade.shares * trade.price).toFixed(2);
            const timestamp = trade.timestamp ? new Date(trade.timestamp).toLocaleTimeString() : 'N/A';
            const isClose = trade.type?.startsWith('CLOSE_');
            const pl = trade.profitLoss;

            return (
              <tr key={index} className={trade.type.includes('LONG') ? 'long-trade' : 'short-trade'}>
                <td>{timestamp}</td>
                <td>{stockInfo.ticker}</td>
                <td>{trade.type}</td>
                <td>{trade.shares}</td>
                <td>${trade.price?.toFixed(2)}</td>
                <td>${tradeValue}</td>
                <td>{isClose && pl != null
                  ? <span style={{ color: pl >= 0 ? '#00ff88' : '#ff4444' }}>
                      {pl >= 0 ? '+' : ''}${pl.toFixed(2)}
                    </span>
                  : '—'}</td>
                <td>{trade.username || trade.userId?.substring(0, 8) || 'Unknown'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };
  
  // Function to perform reset
  const performReset = async (resetType) => {
    // Use entered adminKey or fall back to stored admin token
    const keyToUse = adminKey || getAdminToken();
    if (!keyToUse) {
      setResetResult({
        message: 'Admin key is required',
        type: 'error'
      });
      return;
    }
    
    // Confirm based on reset type
    let confirmMessage = '';
    if (resetType === 'soft') {
      confirmMessage = 'Are you sure you want to reset all prices and balances? This cannot be undone!';
    } else if (resetType === 'hard') {
      confirmMessage = 'WARNING: This will delete ALL game data except QR codes. Are you absolutely sure?';
    } else if (resetType === 'complete') {
      confirmMessage = 'EXTREME WARNING: This will delete EVERYTHING including all QR codes. Are you absolutely sure?';
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Double confirm for destructive actions
    if (resetType === 'hard' || resetType === 'complete') {
      const secondConfirmMessage = 'FINAL WARNING: All user accounts, positions, and history will be permanently deleted. Continue?';
      if (!window.confirm(secondConfirmMessage)) {
        return;
      }
    }
    
    try {
      setIsResetting(true);
      setResetResult({
        message: 'Processing reset... Please wait...',
        type: 'info'
      });
      
      const response = await adminApi.post('/api/admin/reset-game', {
        resetType,
        adminKey: keyToUse
      });
      
      if (response.data.success) {
        setResetResult({
          message: response.data.message,
          type: 'success'
        });
        
        // If it's a complete reset, let the user know it will take time
        if (resetType === 'complete') {
          setResetResult(prev => ({
            message: `${prev.message} QR code regeneration is in progress. This may take several minutes to complete.`,
            type: 'success'
          }));
        }
      } else {
        setResetResult({
          message: `Error: ${response.data.message}`,
          type: 'error'
        });
      }
    } catch (error) {
      setResetResult({
        message: `Error: ${error.response?.data?.message || error.message}`,
        type: 'error'
      });
    } finally {
      setIsResetting(false);
    }
  };

  // =============================================================================
  // NEW ADMIN FEATURES API FUNCTIONS
  // =============================================================================

  // Start Party (Pre-market countdown)
  const startParty = async () => {
    setIsStartingParty(true);
    try {
      const response = await adminApi.post('/api/admin/show/premarket', {
        action: 'start',
        minutes: partyState.premarketMinutes
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'premarket' }));
        setMessage({
          text: `Party started! Pre-market countdown: ${partyState.premarketMinutes} minutes`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting party:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting party',
        type: 'error'
      });
    } finally {
      setIsStartingParty(false);
    }
  };

  // Start Orientation
  const startOrientation = async () => {
    try {
      const response = await adminApi.post('/api/admin/show/orientation', {
        action: 'start'
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'orientation' }));
        setMessage({
          text: 'Orientation video started!',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting orientation:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting orientation',
        type: 'error'
      });
    }
  };

  // Start Market Open Countdown (short countdown before bell)
  const startMarketCountdown = async (seconds = 10) => {
    try {
      const response = await adminApi.post('/api/admin/show/countdown', {
        seconds: seconds,
        autoOpenMarket: true
      });

      if (response.data.success) {
        setMessage({
          text: `Market opening in ${seconds} seconds!`,
          type: 'success'
        });
        // Market will auto-open after countdown
        setTimeout(() => {
          setPartyState(prev => ({ ...prev, phase: 'trading' }));
          fetchMarketStatus();
        }, (seconds + 1) * 1000);
      }
    } catch (error) {
      console.error('Error starting market countdown:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting countdown',
        type: 'error'
      });
    }
  };

  // Modify active countdown (add/subtract/cancel)
  const modifyCountdown = async (action, seconds = 0) => {
    try {
      const response = await adminApi.post('/api/admin/show/countdown/modify', {
        action,
        seconds
      });
      if (response.data.success) {
        setMessage({ text: response.data.message, type: 'success' });
      }
    } catch (error) {
      console.error('Error modifying countdown:', error);
      setMessage({ text: error.response?.data?.message || 'Error modifying countdown', type: 'error' });
    }
  };

  // Start Market Close Countdown (short countdown before closing bell)
  const startCloseCountdown = async (seconds = 10) => {
    try {
      const response = await adminApi.post('/api/admin/show/close-countdown', {
        seconds: seconds,
        autoCloseMarket: true
      });
      if (response.data.success) {
        setMessage({
          text: `Market closing in ${seconds} seconds!`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting close countdown:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting close countdown',
        type: 'error'
      });
    }
  };

  // Start Trading Block Countdown
  const startTradingBlockCountdown = async (seconds = 2700) => {
    try {
      const response = await adminApi.post('/api/admin/trading-block-countdown', {
        seconds: seconds,
        blockNumber: partyState.tradingBlockNumber || 1
      });
      if (response.data.success) {
        const minutes = Math.floor(seconds / 60);
        setMessage({
          text: `Trading block countdown started: ${minutes} min`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting trading block countdown:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting trading block countdown',
        type: 'error'
      });
    }
  };

  // Quick Macro Game Countdown (for testing - doesn't pause trading)
  const quickMacroCountdown = async (seconds = 30) => {
    try {
      const response = await adminApi.post('/api/admin/show/macro-countdown', {
        gameName: partyState.macroGameName || 'Test Game',
        seconds: seconds
      });
      if (response.data.success) {
        setMessage({
          text: `Macro game countdown: ${seconds}s`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting quick macro countdown:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting macro countdown',
        type: 'error'
      });
    }
  };

  // Force phase transition (emergency/manual override)
  const forcePhase = async (phase, additionalData = {}) => {
    try {
      const response = await adminApi.post('/api/admin/party-state', {
        phase,
        ...additionalData
      });
      if (response.data.success) {
        setMessage({ text: `Forced transition to: ${phase}`, type: 'success' });
        setPartyState(prev => ({ ...prev, phase }));
      }
    } catch (error) {
      console.error('Error forcing phase:', error);
      setMessage({ text: error.response?.data?.message || 'Error forcing phase', type: 'error' });
    }
  };

  // Restart party flow (back to beginning)
  const restartPartyFlow = async () => {
    if (!window.confirm('Are you sure you want to restart the party flow? This will reset to the beginning (Registration + Premarket) but will NOT delete any game data.')) {
      return;
    }
    try {
      const response = await adminApi.post('/api/admin/party-restart');
      if (response.data.success) {
        setMessage({ text: '🔄 Party flow restarted! Ready to begin again.', type: 'success' });
        setPartyState(prev => ({
          ...prev,
          phase: 'not_started',
          tradingBlockNumber: 1,
          completedGames: [],
          currentMacroGame: null
        }));
        fetchMarketStatus();
      }
    } catch (error) {
      console.error('Error restarting party flow:', error);
      setMessage({ text: error.response?.data?.message || 'Error restarting party flow', type: 'error' });
    }
  };

  // Send generic show event
  const sendShowEvent = async (eventName, data = {}) => {
    try {
      const response = await adminApi.post(`/api/admin/show/${eventName}`, data);
      if (response.data.success) {
        setMessage({ text: `${eventName} event sent`, type: 'success' });
      }
    } catch (error) {
      console.error(`Error sending ${eventName}:`, error);
      setMessage({ text: error.response?.data?.message || `Error: ${eventName}`, type: 'error' });
    }
  };

  // Send broadcast event (for LED panels / ESP32)
  const sendBroadcastEvent = async (endpoint, data = {}) => {
    try {
      const response = await adminApi.post(`/api/admin/events/${endpoint}`, data);
      if (response.data.success) {
        setMessage({ text: 'Event broadcasted', type: 'success' });
      }
    } catch (error) {
      console.error('Error broadcasting event:', error);
      setMessage({ text: error.response?.data?.message || 'Error broadcasting', type: 'error' });
    }
  };

  // Send warning countdown (10min, 5min, 3min, 1min warnings)
  const sendWarning = async (type, minutes, additionalData = {}) => {
    try {
      const response = await adminApi.post('/api/admin/show/warning', {
        type,
        minutes,
        data: additionalData
      });
      if (response.data.success) {
        setMessage({ text: `${minutes}min warning sent`, type: 'success' });
      }
    } catch (error) {
      console.error('Error sending warning:', error);
      setMessage({ text: error.response?.data?.message || 'Error sending warning', type: 'error' });
    }
  };

  // Set Broadcast URL
  const setBroadcastUrl = async () => {
    try {
      const response = await adminApi.post('/api/admin/broadcast-url', {
        url: partyState.broadcastUrl
      });

      if (response.data.success) {
        setMessage({
          text: partyState.broadcastUrl ? 'Broadcast URL set!' : 'Broadcast URL cleared',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error setting broadcast URL:', error);
      setMessage({
        text: error.response?.data?.message || 'Error setting broadcast URL',
        type: 'error'
      });
    }
  };

  // Start Macro Game Countdown
  const startMacroGameCountdown = async () => {
    setIsActionLoading(true);
    try {
      // First pause trading
      await adminApi.post('/api/admin/trading/pause');

      // Then start the macro game countdown
      const response = await adminApi.post('/api/admin/show/macro-countdown', {
        gameName: partyState.macroGameName,
        seconds: partyState.macroGameSeconds
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'macro_game' }));
        setMessage({
          text: `Macro game "${partyState.macroGameName}" countdown started! Trading paused.`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting macro game:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting macro game',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Start Macro Game Countdown with specific game name and seconds
  const startMacroGameCountdownWithName = async (gameName, seconds) => {
    setIsActionLoading(true);
    try {
      const response = await adminApi.post('/api/admin/show/macro-countdown', {
        gameName: gameName,
        seconds: seconds
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'macro_game', macroGameName: gameName }));
        setMessage({
          text: `${gameName} countdown started (${seconds}s)! Trading will pause when countdown ends.`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting macro game:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting macro game',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Start Trading Phase (1-4)
  const startTradingPhase = async (phaseNumber) => {
    setIsActionLoading(true);
    try {
      const response = await adminApi.post('/api/admin/trading-phase/start', {
        phase: phaseNumber
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'trading', tradingBlockNumber: phaseNumber }));
        setMessage({
          text: `Phase ${phaseNumber} started! Trading resumed.`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error starting trading phase:', error);
      setMessage({
        text: error.response?.data?.message || 'Error starting trading phase',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Resume Trading (after macro game)
  const resumeTrading = async () => {
    setIsActionLoading(true);
    try {
      // Clear the macro countdown
      await adminApi.post('/api/admin/show/macro-countdown/clear');

      // Resume trading
      const response = await adminApi.post('/api/admin/trading/resume');

      if (response.data.success) {
        setPartyState(prev => {
          // If coming from a macro game, track the completed game and increment block
          if (prev.phase === 'macro_game') {
            const newCompletedGames = [...prev.completedGames];
            if (!newCompletedGames.includes(prev.macroGameName)) {
              newCompletedGames.push(prev.macroGameName);
            }
            return {
              ...prev,
              phase: 'trading',
              completedGames: newCompletedGames,
              tradingBlockNumber: Math.min(prev.tradingBlockNumber + 1, 4)
            };
          }
          return { ...prev, phase: 'trading' };
        });
        setMessage({
          text: 'Trading resumed!',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error resuming trading:', error);
      setMessage({
        text: error.response?.data?.message || 'Error resuming trading',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Pause Trading (manual)
  const pauseTrading = async () => {
    setIsActionLoading(true);
    try {
      const response = await adminApi.post('/api/admin/trading/pause');
      if (response.data.success) {
        setMessage({
          text: 'Trading paused',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error pausing trading:', error);
      setMessage({
        text: error.response?.data?.message || 'Error pausing trading',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Close Market
  const closeMarket = async () => {
    setIsActionLoading(true);
    try {
      const response = await adminApi.post('/api/admin/market/toggle', {
        action: 'close'
      });

      if (response.data.success) {
        setPartyState(prev => ({ ...prev, phase: 'ended' }));
        setMarketState(response.data.marketState);
        setMessage({
          text: 'Market closed! Party ending...',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error closing market:', error);
      setMessage({
        text: error.response?.data?.message || 'Error closing market',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Show Final Results
  const showFinalResults = async () => {
    setIsActionLoading(true);
    try {
      const response = await adminApi.post('/api/admin/show/final-results');

      if (response.data.success) {
        setMessage({
          text: 'Final results displayed!',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error showing final results:', error);
      setMessage({
        text: error.response?.data?.message || 'Error showing final results',
        type: 'error'
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await adminApi.get('/api/admin/dashboard');
      if (response.data.success) {
        setDashboardData(response.data.dashboard);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Fetch market status
  const fetchMarketStatus = async () => {
    try {
      const response = await adminApi.get('/api/admin/market/status');
      if (response.data.success) {
        setMarketState(response.data.marketState);
      }
    } catch (error) {
      console.error('Error fetching market status:', error);
    }
  };

  // Toggle market (opening/closing bell)
  const toggleMarket = async (action) => {
    try {
      const response = await adminApi.post('/api/admin/market/toggle', {
        action: action
      });
      
      if (response.data.success) {
        setMarketState(response.data.marketState);
        setMessage({
          text: response.data.message,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error toggling market:', error);
      setMessage({
        text: error.response?.data?.message || 'Error toggling market',
        type: 'error'
      });
    }
  };

  // Generate QR code
  const generateQRCode = async () => {
    if (!qrGenerator.amount || !qrGenerator.reason) {
      setMessage({
        text: 'Please fill in all fields',
        type: 'error'
      });
      return;
    }

    try {
      const response = await adminApi.post('/api/admin/generate-qr', {
        type: qrGenerator.type,
        amount: parseFloat(qrGenerator.amount),
        reason: qrGenerator.reason
      });

      if (response.data.success) {
        setQrGenerator(prev => ({
          ...prev,
          generatedQR: response.data
        }));
        setMessage({
          text: response.data.message,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      setMessage({
        text: error.response?.data?.message || 'Error generating QR code',
        type: 'error'
      });
    }
  };

  // Announce big game
  const announceBigGame = async () => {
    if (!bigGameData.gameName || !bigGameData.announcement) {
      setMessage({
        text: 'Please fill in game name and announcement',
        type: 'error'
      });
      return;
    }

    try {
      const response = await adminApi.post('/api/admin/big-game/announce', {
        gameName: bigGameData.gameName,
        announcement: bigGameData.announcement,
        pauseTrading: bigGameData.pauseTrading
      });

      if (response.data.success) {
        setMessage({
          text: response.data.message,
          type: 'success'
        });
        // Reset form
        setBigGameData({
          gameName: '',
          announcement: '',
          pauseTrading: false
        });
      }
    } catch (error) {
      console.error('Error announcing big game:', error);
      setMessage({
        text: error.response?.data?.message || 'Error announcing big game',
        type: 'error'
      });
    }
  };

  // Fetch macro control state
  const fetchMacroControlState = async () => {
    try {
      const response = await adminApi.get('/api/admin/market/control-state');
      if (response.data.success) {
        setMacroControlState(response.data);
      }
    } catch (error) {
      console.error('Error fetching macro control state:', error);
    }
  };

  // Pause events (for macro games)
  const pauseEvents = async () => {
    try {
      const response = await adminApi.post('/api/admin/events/pause');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMessage({
          text: 'Market events paused successfully',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error pausing events:', error);
      setMessage({
        text: error.response?.data?.message || 'Error pausing events',
        type: 'error'
      });
    }
  };

  // Resume events
  const resumeEvents = async () => {
    try {
      const response = await adminApi.post('/api/admin/events/resume');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMessage({
          text: 'Market events resumed successfully',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error resuming events:', error);
      setMessage({
        text: error.response?.data?.message || 'Error resuming events',
        type: 'error'
      });
    }
  };

  // Pause entire market
  const pauseMarket = async () => {
    try {
      const response = await adminApi.post('/api/admin/market/pause');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMarketState(prev => ({ ...prev, tradingEnabled: false }));
        setMessage({
          text: 'Entire market paused successfully',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error pausing market:', error);
      setMessage({
        text: error.response?.data?.message || 'Error pausing market',
        type: 'error'
      });
    }
  };

  // Resume entire market
  const resumeMarket = async () => {
    try {
      const response = await adminApi.post('/api/admin/market/resume');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMarketState(prev => ({ ...prev, tradingEnabled: true }));
        setMessage({
          text: 'Entire market resumed successfully',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error resuming market:', error);
      setMessage({
        text: error.response?.data?.message || 'Error resuming market',
        type: 'error'
      });
    }
  };

  const pausePrices = async () => {
    try {
      const response = await adminApi.post('/api/admin/prices/pause');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMessage({
          text: '📊 Price updates paused',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error pausing prices:', error);
      setMessage({
        text: error.response?.data?.message || 'Error pausing price updates',
        type: 'error'
      });
    }
  };

  const resumePrices = async () => {
    try {
      const response = await adminApi.post('/api/admin/prices/resume');
      if (response.data.success) {
        setMacroControlState(response.data.marketState);
        setMessage({
          text: '📊 Price updates resumed',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error resuming prices:', error);
      setMessage({
        text: error.response?.data?.message || 'Error resuming price updates',
        type: 'error'
      });
    }
  };

  // Check system health
  const checkSystemHealth = async () => {
    const health = {
      database: 'healthy',
      server: 'healthy',
      websocket: socket.get()?.connected ? 'healthy' : 'disconnected',
      lastUpdate: Date.now()
    };

    try {
      // Test database by fetching dashboard data
      await adminApi.get('/api/admin/dashboard');
      health.database = 'healthy';
    } catch (error) {
      health.database = 'error';
    }

    setSystemHealth(health);
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>🎮 Party Admin Control Center</h1>
        <div className="system-health-bar">
          <div className="health-indicator">
            <span className={`health-dot ${systemHealth.database}`}></span>
            Database: {systemHealth.database}
          </div>
          <div className="health-indicator">
            <span className={`health-dot ${systemHealth.websocket}`}></span>
            WebSocket: {systemHealth.websocket}
          </div>
          <div className="health-indicator">
            <span className="health-dot healthy"></span>
            Last Update: {new Date(systemHealth.lastUpdate).toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Grid Navigation - Everything visible at once */}
      <div className="admin-nav-grid">
        <div className="nav-section">
          <div className="nav-section-label">PARTY</div>
          <div className="nav-buttons">
            <button className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => navigateToTab('dashboard')}>📊 Dashboard</button>
            <button className={`nav-btn ${activeTab === 'bell' ? 'active' : ''}`} onClick={() => navigateToTab('bell')}>🔔 Bell</button>
            <button className={`nav-btn ${activeTab === 'register' ? 'active' : ''}`} onClick={() => navigateToTab('register')}>📸 Register</button>
            <button className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => navigateToTab('users')}>👥 Users</button>
            <button className={`nav-btn ${activeTab === 'qr-gen' ? 'active' : ''}`} onClick={() => navigateToTab('qr-gen')}>📱 QR Gen</button>
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-section-label">MACRO GAMES</div>
          <div className="nav-buttons">
            <button className={`nav-btn ${activeTab === 'battleship' ? 'active' : ''}`} onClick={() => navigateToTab('battleship')}>💼 Portfolio Warfare</button>
            <button className={`nav-btn ${activeTab === 'briefcase' ? 'active' : ''}`} onClick={() => navigateToTab('briefcase')}>💰 A Brief Case</button>
            <button className={`nav-btn ${activeTab === 'minigolf' ? 'active' : ''}`} onClick={() => navigateToTab('minigolf')}>⛳ Dead Cat Bounce</button>
            <button className={`nav-btn ${activeTab === 'tunnel' ? 'active' : ''}`} onClick={() => navigateToTab('tunnel')}>🌀 Liquidity Crisis</button>
            <button className={`nav-btn ${activeTab === 'teams' ? 'active' : ''}`} onClick={() => navigateToTab('teams')}>👥 Teams</button>
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-section-label">MONITOR</div>
          <div className="nav-buttons">
            <button className={`nav-btn ${activeTab === 'trades' ? 'active' : ''}`} onClick={() => navigateToTab('trades')}>💰 Trades</button>
            <button className={`nav-btn ${activeTab === 'events' ? 'active' : ''}`} onClick={() => navigateToTab('events')}>📢 Events</button>
            <button className={`nav-btn ${activeTab === 'market' ? 'active' : ''}`} onClick={() => navigateToTab('market')}>📉 Market</button>
            <button className={`nav-btn ${activeTab === 'concentration' ? 'active' : ''}`} onClick={() => navigateToTab('concentration')}>🎯 Concentration</button>
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-section-label">HARDWARE</div>
          <div className="nav-buttons">
            <button className={`nav-btn ${activeTab === 'led' ? 'active' : ''}`} onClick={() => navigateToTab('led')}>💡 LED</button>
            <button className={`nav-btn ${activeTab === 'tester' ? 'active' : ''}`} onClick={() => navigateToTab('tester')}>📡 Tester</button>
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-section-label">ADMIN</div>
          <div className="nav-buttons">
            <button className={`nav-btn ${activeTab === 'qr-codes' ? 'active' : ''}`} onClick={() => navigateToTab('qr-codes')}>🎫 QR Codes</button>
            <button className={`nav-btn ${activeTab === 'ceos' ? 'active' : ''}`} onClick={() => navigateToTab('ceos')}>🧑‍💼 CEOs</button>
            <button className={`nav-btn ${activeTab === 'videos' ? 'active' : ''}`} onClick={() => navigateToTab('videos')}>🎬 Videos</button>
            <button className={`nav-btn ${activeTab === 'embeds' ? 'active' : ''}`} onClick={() => navigateToTab('embeds')}>🖥️ Embeds</button>
            <button className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => navigateToTab('settings')}>⚙️ Settings</button>
            <button className={`nav-btn ${activeTab === 'observability' ? 'active' : ''}`} onClick={() => navigateToTab('observability')}>🔍 Health</button>
            <button className={`nav-btn danger ${activeTab === 'reset' ? 'active' : ''}`} onClick={() => navigateToTab('reset')}>⚠️ Reset</button>
            <button className="nav-btn external" onClick={() => window.open('/party-entrance-qr', '_blank')}>🎫 Entrance↗</button>
            <button className="nav-btn external" onClick={() => window.open('/spectate/ceremony', '_blank')}>🏆 Ceremony↗</button>
          </div>
        </div>
      </div>
      
      {/* Message display */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      {/* Error display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>Dismiss</button>
        </div>
      )}
      
      {/* DASHBOARD TAB */}
      {activeTab === 'dashboard' && (
        <div className="overview-tab">
          <h2>📊 Party Status Overview</h2>

          {/* Party Timeline */}
          <AdminTimeline
            currentPhase={partyState.phase}
            macroGameName={partyState.macroGameName}
            tradingBlockNumber={partyState.tradingBlockNumber}
            completedGames={partyState.completedGames}
          />

          {/* Live State Debug Panel */}
          <details style={{
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '8px',
            padding: '0',
            marginBottom: '15px',
            fontFamily: 'monospace',
            fontSize: '12px'
          }}>
            <summary style={{
              padding: '10px 15px',
              cursor: 'pointer',
              color: '#58a6ff',
              fontWeight: 'bold',
              fontSize: '14px',
              userSelect: 'none'
            }}>
              📡 Live Server State
              <span style={{
                marginLeft: '10px',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                background: marketState.isOpen ? '#0d3320' : '#3d1418',
                color: marketState.isOpen ? '#3fb950' : '#f85149',
                border: `1px solid ${marketState.isOpen ? '#238636' : '#da3633'}`
              }}>
                Market {marketState.isOpen ? 'OPEN' : 'CLOSED'}
              </span>
              <span style={{
                marginLeft: '6px',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                background: '#1c2433',
                color: '#79c0ff',
                border: '1px solid #1f6feb'
              }}>
                {partyState.phase?.replace(/_/g, ' ').toUpperCase()}
              </span>
            </summary>
            <div style={{padding: '12px 15px', borderTop: '1px solid #30363d'}}>
              <pre style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                color: '#c9d1d9',
                lineHeight: '1.6'
              }}>{JSON.stringify({
                market: {
                  isOpen: marketState.isOpen,
                  tradingEnabled: marketState.tradingEnabled,
                  openedAt: marketState.openedAt || null
                },
                party: {
                  phase: partyState.phase,
                  tradingBlock: `${partyState.tradingBlockNumber}/4`,
                  sessionName: ['Asian Session', 'European Session', 'Canadian Session', 'Closing Bell'][((partyState.tradingBlockNumber || 1) - 1)] || 'Unknown',
                  completedGames: partyState.completedGames || [],
                  macroGame: ['Portfolio Warfare', 'Dead Cat Bounce', 'A Brief Case'][(partyState.tradingBlockNumber || 1) - 1] || null
                },
                countdown: liveCountdown.active ? {
                  type: liveCountdown.type,
                  label: liveCountdown.label,
                  secondsRemaining: liveCountdown.secondsRemaining,
                  totalSeconds: liveCountdown.totalSeconds
                } : 'none',
                controls: {
                  tradingPaused: macroControlState.tradingPaused,
                  eventsPaused: macroControlState.eventsPaused,
                  pricesPaused: macroControlState.pricesPaused
                }
              }, null, 2)}</pre>
            </div>
          </details>

          {/* Party Control Panel */}
          <div className="party-control-panel">
            <h3>🎉 Party Control</h3>

            {/* Phase Status Banner */}
            <div className={`party-phase-banner phase-${partyState.phase}`}>
              <span className="phase-indicator"></span>
              <span className="phase-text">{partyState.phase.replace(/_/g, ' ').toUpperCase()}</span>
            </div>

            {/* Live Status Display */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: liveCountdown.active ? '1fr 1fr' : '1fr',
              gap: '15px',
              margin: '15px 0',
              padding: '15px',
              background: '#1a1a2e',
              borderRadius: '12px',
              border: '1px solid #333'
            }}>
              {/* Active Countdown */}
              {liveCountdown.active && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  background: liveCountdown.type === 'market-open' ? 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)' :
                             liveCountdown.type === 'market-close' ? 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' :
                             'linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%)',
                  borderRadius: '10px',
                  border: '2px solid',
                  borderColor: liveCountdown.type === 'market-open' ? '#10b981' :
                               liveCountdown.type === 'market-close' ? '#ef4444' : '#8b5cf6'
                }}>
                  <div style={{fontSize: '12px', color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '2px'}}>
                    {liveCountdown.label}
                  </div>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    color: '#fff',
                    textShadow: '0 0 20px rgba(255,255,255,0.3)'
                  }}>
                    {formatCountdown(liveCountdown.secondsRemaining)}
                  </div>
                  <div style={{
                    marginTop: '10px',
                    height: '6px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(liveCountdown.secondsRemaining / liveCountdown.totalSeconds) * 100}%`,
                      background: liveCountdown.type === 'market-open' ? '#10b981' :
                                 liveCountdown.type === 'market-close' ? '#ef4444' : '#8b5cf6',
                      transition: 'width 1s linear'
                    }} />
                  </div>
                </div>
              )}

              {/* What's Next */}
              <div style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                borderRadius: '10px',
                border: '1px solid #475569'
              }}>
                <div style={{fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px'}}>
                  {liveCountdown.active ? "After Countdown" : "What's Next"}
                </div>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#f1f5f9'}}>
                  {getNextPhaseInfo().next}
                </div>
                {partyState.tradingBlockNumber > 0 && (
                  <div style={{fontSize: '13px', color: '#64748b', marginTop: '5px'}}>
                    Block: <span style={{color: '#60a5fa', fontWeight: 'bold'}}>{partyState.tradingBlockNumber}/4</span>
                  </div>
                )}
                <button
                  onClick={() => toggleMarket(marketState.isOpen ? 'close' : 'open')}
                  style={{
                    marginTop: '12px',
                    padding: '14px 24px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    border: '3px solid',
                    borderColor: marketState.isOpen ? '#fca5a5' : '#6ee7b7',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    width: '100%',
                    background: marketState.isOpen
                      ? 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)'
                      : 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
                    color: '#fff',
                    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                    boxShadow: marketState.isOpen
                      ? '0 0 15px rgba(239,68,68,0.4)'
                      : '0 0 15px rgba(16,185,129,0.4)',
                    letterSpacing: '1px'
                  }}
                >
                  {marketState.isOpen ? '🔴 CLOSE MARKET' : '🟢 OPEN MARKET'}
                </button>
              </div>
            </div>

            {/* ================================================================= */}
            {/* PARTY CONTROL - Matches PARTY_FLOW.md and AdminTimeline exactly */}
            {/* ================================================================= */}

            {/* STEP 1: Registration + Premarket */}
            <div className="party-section" style={{background: 'linear-gradient(135deg, #1a1a3e 0%, #2a1a4e 100%)', border: '2px solid #6366f1'}}>
              <h4>📝 Step 1: Registration + Premarket</h4>
              <div style={{background: 'rgba(99, 102, 241, 0.15)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#a5b4fc', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '4px'}}>
                  <strong>🚀 START PARTY</strong> = Opens registration. Guests can now sign up. <span style={{color: '#fbbf24'}}>Does NOT start any timer.</span>
                </div>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(148, 163, 184, 0.15)', borderRadius: '4px'}}>
                  <strong>📢 WARNING BUTTONS (10m/5m/1m)</strong> = Pops up a toast notification on guest phones saying "Orientation in X min".<br/>
                  <span style={{color: '#fbbf24'}}>⚠️ ONE-TIME POPUP - no timer starts, nothing auto-transitions. Just alerts guests.</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: '4px'}}>
                  <strong>🎬 PLAY ORIENTATION</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY moves to Step 2.</span> Guests see orientation video screen.
                </div>
              </div>

              {partyState.phase === 'not_started' ? (
                <button
                  className="phase-btn start-party"
                  style={{fontSize: '16px', padding: '15px 30px', width: '100%', background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'}}
                  onClick={async () => {
                    await sendShowEvent('registration', { action: 'start' });
                    await startParty();
                  }}
                  disabled={isStartingParty}
                >
                  {isStartingParty ? '⏳ Starting...' : '🚀 START PARTY (opens registration)'}
                </button>
              ) : (
                <div style={{padding: '15px', background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', borderRadius: '8px', color: 'white', textAlign: 'center', marginBottom: '10px'}}>
                  ✓ Party Started — Phase: <strong>{partyState.phase.replace(/_/g, ' ').toUpperCase()}</strong>
                </div>
              )}

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px'}}>
                <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px'}}>
                  <div style={{fontSize: '11px', color: '#94a3b8', marginBottom: '5px'}}>📢 Send Warning (notification only, no timer)</div>
                  <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                    <button className="phase-btn event" onClick={() => sendWarning('orientation', 10)}>10m</button>
                    <button className="phase-btn event" onClick={() => sendWarning('orientation', 5)}>5m</button>
                    <button className="phase-btn event" onClick={() => sendWarning('orientation', 1)}>1m</button>
                  </div>
                </div>
                <button className="phase-btn orientation" style={{height: 'auto', background: '#22c55e'}} onClick={() => sendShowEvent('orientation', { action: 'start' })}>
                  🎬 PLAY ORIENTATION<br/><span style={{fontSize: '10px'}}>(instant → Step 2)</span>
                </button>
              </div>
            </div>

            {/* STEP 2: Orientation → Opening Bell */}
            <div className="party-section" style={{border: '2px solid #22c55e'}}>
              <h4>⏱️ Step 2: Orientation → Opening Bell</h4>
              <div style={{background: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#86efac', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(148, 163, 184, 0.15)', borderRadius: '4px'}}>
                  <strong>✓ END ORIENTATION</strong> = Hides orientation video screen only. <span style={{color: '#fbbf24'}}>Does NOT open market.</span><br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>After clicking: You're still in PREMARKET. Guests are waiting. Nothing happens until you click a countdown button below.</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)'}}>
                  <strong style={{color: '#ef4444'}}>🔔 COUNTDOWN BUTTONS (3:00/2:00/1:00/0:30/0:10)</strong> = <span style={{color: '#ef4444', fontWeight: 'bold'}}>STARTS A HARD TIMER.</span><br/>
                  • Guests see "Opening Bell in X:XX" countdown on their phones<br/>
                  • ESP32 panels show countdown visuals<br/>
                  • <span style={{color: '#22c55e', fontWeight: 'bold'}}>WHEN TIMER HITS ZERO → 🔔 Bell sounds + Market OPENS + Phase 1 STARTS!</span><br/>
                  <strong style={{color: '#fbbf24'}}>NOW</strong> = Opens market immediately (no countdown, still plays bell)
                </div>
              </div>
              <div className="party-phase-buttons" style={{marginBottom: '10px'}}>
                <button className="phase-btn" style={{background: '#475569'}} onClick={() => sendShowEvent('orientation', { action: 'end' })}>
                  ✓ End Orientation (hides video, stays in premarket)
                </button>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#fbbf24', marginBottom: '5px'}}>
                  📢 OPTIONAL: Send "Market Opens Soon" Warning (no timer, just notification)
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  Pops toast on all phones. Use if there's a gap after orientation before you start the countdown.
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn event" onClick={() => sendWarning('market-open', 10)}>10m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-open', 5)}>5m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-open', 3)}>3m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-open', 1)}>1m</button>
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#ef4444', marginBottom: '8px', fontWeight: 'bold'}}>
                  🔔 Opening Bell Countdown — Pick how long guests wait:
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  When timer ends: Bell sounds + Market opens + Phase 1 (Asian) starts + 30-min visual timer begins
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn countdown" style={{background: '#22c55e'}} onClick={() => startMarketCountdown(180)}>3:00</button>
                  <button className="phase-btn countdown" style={{background: '#22c55e'}} onClick={() => startMarketCountdown(120)}>2:00</button>
                  <button className="phase-btn countdown" style={{background: '#22c55e'}} onClick={() => startMarketCountdown(60)}>1:00</button>
                  <button className="phase-btn countdown" style={{background: '#22c55e'}} onClick={() => startMarketCountdown(30)}>0:30</button>
                  <button className="phase-btn countdown" style={{background: '#22c55e'}} onClick={() => startMarketCountdown(10)}>0:10</button>
                  <button className="phase-btn open-market" onClick={() => toggleMarket('open')}>NOW</button>
                </div>
              </div>
            </div>

            {/* STEP 3: Phase 1 (Asian) → Portfolio Warfare */}
            <div className="party-section" style={{border: partyState.tradingBlockNumber === 1 && marketState.isOpen ? '3px solid #00bcd4' : '2px solid #00bcd4', boxShadow: partyState.tradingBlockNumber === 1 && marketState.isOpen ? '0 0 15px rgba(0, 188, 212, 0.5)' : 'none'}}>
              <h4>🌏 Step 3: Phase 1 (Asian Session) {partyState.tradingBlockNumber === 1 && marketState.isOpen && <span style={{background: '#00bcd4', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(0, 188, 212, 0.15)', border: '1px solid rgba(0, 188, 212, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#67e8f9', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#22c55e'}}>HOW YOU GOT HERE:</strong> Opening Bell countdown ended → Market opened → This phase started automatically.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>A 30-min visual timer started automatically when this phase began (SOFT timer = just a guide, trading continues past zero).</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)'}}>
                  <strong style={{color: '#ef4444'}}>🔔 COUNTDOWN BUTTONS (3:00/2:00/1:00/0:30)</strong> = <span style={{color: '#ef4444', fontWeight: 'bold'}}>STARTS A HARD TIMER.</span><br/>
                  • Guests see "Portfolio Warfare in X:XX" on their phones<br/>
                  • ESP32 panels show countdown + game name<br/>
                  • <span style={{color: '#a855f7', fontWeight: 'bold'}}>WHEN TIMER HITS ZERO → Trading PAUSES + time for game!</span><br/>
                  <strong style={{color: '#fbbf24'}}>NOW</strong> = Pauses trading immediately (no countdown)
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#fbbf24', marginBottom: '5px'}}>
                  📢 OPTIONAL: Send "Game Soon" Warning (no timer, just notification)
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  Pops toast on all phones. Use if you're still setting up the game and want to give heads up.
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 10, { gameName: 'Portfolio Warfare' })}>10m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 5, { gameName: 'Portfolio Warfare' })}>5m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 3, { gameName: 'Portfolio Warfare' })}>3m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 1, { gameName: 'Portfolio Warfare' })}>1m</button>
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#ef4444', marginBottom: '8px', fontWeight: 'bold'}}>
                  🔔 Portfolio Warfare Countdown — Pick how long before game:
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  When timer ends: Trading pauses + guests prepare for game
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn countdown" style={{background: '#a855f7'}} onClick={() => startMacroGameCountdownWithName('Portfolio Warfare', 180)}>3:00</button>
                  <button className="phase-btn countdown" style={{background: '#a855f7'}} onClick={() => startMacroGameCountdownWithName('Portfolio Warfare', 120)}>2:00</button>
                  <button className="phase-btn countdown" style={{background: '#a855f7'}} onClick={() => startMacroGameCountdownWithName('Portfolio Warfare', 60)}>1:00</button>
                  <button className="phase-btn countdown" style={{background: '#a855f7'}} onClick={() => startMacroGameCountdownWithName('Portfolio Warfare', 30)}>0:30</button>
                  <button className="phase-btn pause-trading" onClick={pauseTrading}>NOW</button>
                </div>
              </div>
            </div>

            {/* STEP 4: Portfolio Warfare → Phase 2 */}
            <div className="party-section" style={{border: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 1 ? '3px solid #c026d3' : '2px solid #c026d3', boxShadow: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 1 ? '0 0 15px rgba(192, 38, 211, 0.5)' : 'none'}}>
              <h4>💼 Step 4: Portfolio Warfare Game {partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 1 && <span style={{background: '#c026d3', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(192, 38, 211, 0.15)', border: '1px solid rgba(192, 38, 211, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#e879f9', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(192, 38, 211, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#c026d3'}}>HOW YOU GOT HERE:</strong> Portfolio Warfare countdown ended → Trading paused automatically.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Guests cannot trade. Run the game in your venue now!</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(33, 150, 243, 0.2)', borderRadius: '4px', border: '1px solid rgba(33, 150, 243, 0.4)'}}>
                  <strong style={{color: '#2196f3'}}>🇪🇺 GAME DONE BUTTON</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY:</span><br/>
                  • 🔔 "Trading Resumed" bell sound plays<br/>
                  • ESP32 panels show resume animation<br/>
                  • Trading resumes + Phase 2 starts<br/>
                  • New 30-min visual timer starts automatically
                </div>
              </div>
              <button className="phase-btn" style={{width: '100%', background: '#2196f3', fontSize: '14px', padding: '12px'}} onClick={() => startTradingPhase(2)}>
                🇪🇺 GAME DONE → Start Phase 2 (European) — instant + resume bell
              </button>
            </div>

            {/* STEP 5: Phase 2 (European) → Dead Cat Bounce */}
            <div className="party-section" style={{border: partyState.tradingBlockNumber === 2 && marketState.isOpen ? '3px solid #2196f3' : '2px solid #2196f3', boxShadow: partyState.tradingBlockNumber === 2 && marketState.isOpen ? '0 0 15px rgba(33, 150, 243, 0.5)' : 'none'}}>
              <h4>🇪🇺 Step 5: Phase 2 (European Session) {partyState.tradingBlockNumber === 2 && marketState.isOpen && <span style={{background: '#2196f3', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(33, 150, 243, 0.15)', border: '1px solid rgba(33, 150, 243, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#93c5fd', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#22c55e'}}>HOW YOU GOT HERE:</strong> Clicked "Game Done" after Portfolio Warfare → Trading resumed + this phase started.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>A 30-min visual timer started automatically when this phase began (SOFT timer = just a guide).</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)'}}>
                  <strong style={{color: '#ef4444'}}>🔔 COUNTDOWN BUTTONS (3:00/2:00/1:00/0:30)</strong> = <span style={{color: '#ef4444', fontWeight: 'bold'}}>STARTS A HARD TIMER.</span><br/>
                  • Guests see "Dead Cat Bounce in X:XX" on their phones<br/>
                  • <span style={{color: '#00c853', fontWeight: 'bold'}}>WHEN TIMER HITS ZERO → Trading PAUSES + time for game!</span><br/>
                  <strong style={{color: '#fbbf24'}}>NOW</strong> = Pauses trading immediately (no countdown)
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#fbbf24', marginBottom: '5px'}}>
                  📢 OPTIONAL: Send "Game Soon" Warning (no timer, just notification)
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  Pops toast on all phones. Use if you're still setting up the game and want to give heads up.
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 10, { gameName: 'Dead Cat Bounce' })}>10m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 5, { gameName: 'Dead Cat Bounce' })}>5m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 3, { gameName: 'Dead Cat Bounce' })}>3m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 1, { gameName: 'Dead Cat Bounce' })}>1m</button>
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#ef4444', marginBottom: '8px', fontWeight: 'bold'}}>
                  🔔 Dead Cat Bounce Countdown — Pick how long before game:
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  When timer ends: Trading pauses + guests prepare for game
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn countdown" style={{background: '#00c853'}} onClick={() => startMacroGameCountdownWithName('Dead Cat Bounce', 180)}>3:00</button>
                  <button className="phase-btn countdown" style={{background: '#00c853'}} onClick={() => startMacroGameCountdownWithName('Dead Cat Bounce', 120)}>2:00</button>
                  <button className="phase-btn countdown" style={{background: '#00c853'}} onClick={() => startMacroGameCountdownWithName('Dead Cat Bounce', 60)}>1:00</button>
                  <button className="phase-btn countdown" style={{background: '#00c853'}} onClick={() => startMacroGameCountdownWithName('Dead Cat Bounce', 30)}>0:30</button>
                  <button className="phase-btn pause-trading" onClick={pauseTrading}>NOW</button>
                </div>
              </div>
            </div>

            {/* STEP 6: Dead Cat Bounce → Phase 3 */}
            <div className="party-section" style={{border: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 2 ? '3px solid #00c853' : '2px solid #00c853', boxShadow: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 2 ? '0 0 15px rgba(0, 200, 83, 0.5)' : 'none'}}>
              <h4>⛳ Step 6: Dead Cat Bounce Game {partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 2 && <span style={{background: '#00c853', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(0, 200, 83, 0.15)', border: '1px solid rgba(0, 200, 83, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#86efac', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(0, 200, 83, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#00c853'}}>HOW YOU GOT HERE:</strong> Dead Cat Bounce countdown ended → Trading paused automatically.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Guests cannot trade. Run the game in your venue now!</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(244, 67, 54, 0.2)', borderRadius: '4px', border: '1px solid rgba(244, 67, 54, 0.4)'}}>
                  <strong style={{color: '#f44336'}}>🇨🇦 GAME DONE BUTTON</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY:</span><br/>
                  • 🔔 "Trading Resumed" bell sound plays<br/>
                  • ESP32 panels show resume animation<br/>
                  • Trading resumes + Phase 3 starts<br/>
                  • New 30-min visual timer starts automatically
                </div>
              </div>
              <button className="phase-btn" style={{width: '100%', background: '#f44336', fontSize: '14px', padding: '12px'}} onClick={() => startTradingPhase(3)}>
                🇨🇦 GAME DONE → Start Phase 3 (Canadian) — instant + resume bell
              </button>
            </div>

            {/* STEP 7: Phase 3 (Canadian) → A Brief Case */}
            <div className="party-section" style={{border: partyState.tradingBlockNumber === 3 && marketState.isOpen ? '3px solid #f44336' : '2px solid #f44336', boxShadow: partyState.tradingBlockNumber === 3 && marketState.isOpen ? '0 0 15px rgba(244, 67, 54, 0.5)' : 'none'}}>
              <h4>🇨🇦 Step 7: Phase 3 (Canadian Session) {partyState.tradingBlockNumber === 3 && marketState.isOpen && <span style={{background: '#f44336', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(244, 67, 54, 0.15)', border: '1px solid rgba(244, 67, 54, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fca5a5', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#22c55e'}}>HOW YOU GOT HERE:</strong> Clicked "Game Done" after Dead Cat Bounce → Trading resumed + this phase started.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>A 30-min visual timer started automatically when this phase began (SOFT timer = just a guide).</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)'}}>
                  <strong style={{color: '#ef4444'}}>🔔 COUNTDOWN BUTTONS (3:00/2:00/1:00/0:30)</strong> = <span style={{color: '#ef4444', fontWeight: 'bold'}}>STARTS A HARD TIMER.</span><br/>
                  • Guests see "A Brief Case in X:XX" on their phones<br/>
                  • <span style={{color: '#ffd700', fontWeight: 'bold'}}>WHEN TIMER HITS ZERO → Trading PAUSES + time for game!</span><br/>
                  <strong style={{color: '#fbbf24'}}>NOW</strong> = Pauses trading immediately (no countdown)
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#fbbf24', marginBottom: '5px'}}>
                  📢 OPTIONAL: Send "Game Soon" Warning (no timer, just notification)
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  Pops toast on all phones. Use if you're still setting up the game and want to give heads up.
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 10, { gameName: 'A Brief Case' })}>10m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 5, { gameName: 'A Brief Case' })}>5m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 3, { gameName: 'A Brief Case' })}>3m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('macro-game', 1, { gameName: 'A Brief Case' })}>1m</button>
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#ef4444', marginBottom: '8px', fontWeight: 'bold'}}>
                  🔔 A Brief Case Countdown — Pick how long before game:
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  When timer ends: Trading pauses + guests prepare for game
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn countdown" style={{background: '#ffd700', color: '#000'}} onClick={() => startMacroGameCountdownWithName('A Brief Case', 180)}>3:00</button>
                  <button className="phase-btn countdown" style={{background: '#ffd700', color: '#000'}} onClick={() => startMacroGameCountdownWithName('A Brief Case', 120)}>2:00</button>
                  <button className="phase-btn countdown" style={{background: '#ffd700', color: '#000'}} onClick={() => startMacroGameCountdownWithName('A Brief Case', 60)}>1:00</button>
                  <button className="phase-btn countdown" style={{background: '#ffd700', color: '#000'}} onClick={() => startMacroGameCountdownWithName('A Brief Case', 30)}>0:30</button>
                  <button className="phase-btn pause-trading" onClick={pauseTrading}>NOW</button>
                </div>
              </div>
            </div>

            {/* STEP 8: A Brief Case → Power Hour */}
            <div className="party-section" style={{border: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 3 ? '3px solid #ffd700' : '2px solid #ffd700', boxShadow: partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 3 ? '0 0 15px rgba(255, 215, 0, 0.5)' : 'none'}}>
              <h4>💰 Step 8: A Brief Case Game {partyState.phase === 'macro_game' && partyState.tradingBlockNumber === 3 && <span style={{background: '#ffd700', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fde047', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#ffd700'}}>HOW YOU GOT HERE:</strong> A Brief Case countdown ended → Trading paused automatically.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Guests cannot trade. Run the game in your venue now!</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(255, 87, 34, 0.2)', borderRadius: '4px', border: '1px solid rgba(255, 87, 34, 0.4)'}}>
                  <strong style={{color: '#ff5722'}}>⚡ GAME DONE BUTTON</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY:</span><br/>
                  • 🔔 "Trading Resumed" bell sound plays<br/>
                  • ESP32 panels show resume animation<br/>
                  • Trading resumes + Power Hour starts<br/>
                  • 5-min visual timer starts (final frenzy!)
                </div>
              </div>
              <button className="phase-btn" style={{width: '100%', background: '#ff5722', fontSize: '14px', padding: '12px'}} onClick={() => startTradingPhase(4)}>
                ⚡ GAME DONE → Start Phase 4 (Power Hour) — instant + resume bell
              </button>
            </div>

            {/* STEP 9: Power Hour → Closing Bell */}
            <div className="party-section" style={{border: partyState.tradingBlockNumber === 4 && marketState.isOpen ? '3px solid #ff5722' : '2px solid #ff5722', boxShadow: partyState.tradingBlockNumber === 4 && marketState.isOpen ? '0 0 15px rgba(255, 87, 34, 0.5)' : 'none'}}>
              <h4>⚡ Step 9: Phase 4 (Power Hour) {partyState.tradingBlockNumber === 4 && marketState.isOpen && <span style={{background: '#ff5722', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(255, 87, 34, 0.15)', border: '1px solid rgba(255, 87, 34, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fdba74', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#22c55e'}}>HOW YOU GOT HERE:</strong> Clicked "Game Done" after A Brief Case → Trading resumed + Power Hour started.<br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>A 5-min visual timer started automatically (SOFT timer = just a guide, can extend if needed).</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)'}}>
                  <strong style={{color: '#ef4444'}}>🔔 COUNTDOWN BUTTONS (3:00/2:00/1:00/0:30/0:10)</strong> = <span style={{color: '#ef4444', fontWeight: 'bold'}}>STARTS A HARD TIMER.</span><br/>
                  • Guests see "Market Closes in X:XX" on their phones<br/>
                  • ESP32 panels show closing countdown<br/>
                  • <span style={{color: '#ef4444', fontWeight: 'bold'}}>⚠️ WHEN TIMER HITS ZERO → 🔔 Closing bell + Market CLOSES PERMANENTLY!</span><br/>
                  <strong style={{color: '#fbbf24'}}>NOW</strong> = Closes market immediately (still plays closing bell)
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#fbbf24', marginBottom: '5px'}}>
                  📢 OPTIONAL: Send "Last Call" Warning (no timer, just notification)
                </div>
                <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '8px'}}>
                  Pops toast on all phones. Nothing else happens. Use to build tension before you commit to closing.
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn event" onClick={() => sendWarning('market-close', 10)}>10m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-close', 5)}>5m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-close', 3)}>3m</button>
                  <button className="phase-btn event" onClick={() => sendWarning('market-close', 1)}>1m</button>
                </div>
              </div>
              <div style={{background: '#1e293b', padding: '10px', borderRadius: '8px', marginBottom: '10px'}}>
                <div style={{fontSize: '11px', color: '#ef4444', marginBottom: '8px', fontWeight: 'bold'}}>
                  🔔 Closing Bell Countdown — Pick how long guests have left:
                </div>
                <div style={{fontSize: '10px', color: '#fca5a5', marginBottom: '8px'}}>
                  ⚠️ When timer ends: Closing bell sounds + Market CLOSES permanently!
                </div>
                <div style={{display: 'flex', gap: '5px', flexWrap: 'wrap'}}>
                  <button className="phase-btn countdown" style={{background: '#ef4444'}} onClick={() => startCloseCountdown(180)}>3:00</button>
                  <button className="phase-btn countdown" style={{background: '#ef4444'}} onClick={() => startCloseCountdown(120)}>2:00</button>
                  <button className="phase-btn countdown" style={{background: '#ef4444'}} onClick={() => startCloseCountdown(60)}>1:00</button>
                  <button className="phase-btn countdown" style={{background: '#ef4444'}} onClick={() => startCloseCountdown(30)}>0:30</button>
                  <button className="phase-btn countdown" style={{background: '#ef4444'}} onClick={() => startCloseCountdown(10)}>0:10</button>
                  <button className="phase-btn close-market" onClick={closeMarket}>NOW</button>
                </div>
              </div>
            </div>

            {/* STEP 10: After Close → Winners */}
            <div className="party-section" style={{border: partyState.phase === 'ended' || (!marketState.isOpen && partyState.tradingBlockNumber === 4) ? '3px solid #dc2626' : '2px solid #dc2626', boxShadow: partyState.phase === 'ended' || (!marketState.isOpen && partyState.tradingBlockNumber === 4) ? '0 0 15px rgba(220, 38, 38, 0.5)' : 'none'}}>
              <h4>🏁 Step 10: After Market Close {(partyState.phase === 'ended' || (!marketState.isOpen && partyState.tradingBlockNumber === 4)) && <span style={{background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '10px'}}>◀ YOU ARE HERE</span>}</h4>
              <div style={{background: 'rgba(220, 38, 38, 0.15)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fca5a5', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(220, 38, 38, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#dc2626'}}>🔒 MARKET IS CLOSED</strong> — Trading is done! No more trades possible.
                </div>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(0, 230, 118, 0.15)', borderRadius: '4px'}}>
                  <strong style={{color: '#00e676'}}>🌀 LIQUIDITY CRISIS BUTTON</strong> = <span style={{color: '#fbbf24'}}>Starts a 1-min countdown for bonus game.</span><br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Optional! Only if you want to run this bonus game.</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#ffd700'}}>⏭️ SKIP TO WINNERS</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY shows final leaderboard.</span><br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Use this if you're skipping Liquidity Crisis game.</span>
                </div>
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                <button className="phase-btn" style={{background: '#00e676', color: '#000'}} onClick={() => {
                  setPartyState(prev => ({ ...prev, macroGameName: 'Liquidity Crisis' }));
                  startMacroGameCountdownWithName('Liquidity Crisis', 60);
                }}>
                  🌀 Liquidity Crisis (1-min countdown)
                </button>
                <button className="phase-btn" style={{background: '#ffd700', color: '#000'}} onClick={showFinalResults}>
                  ⏭️ Skip → Winners (instant)
                </button>
              </div>
            </div>

            {/* STEP 11: Winners */}
            <div className="party-section" style={{border: '2px solid #ffd700'}}>
              <h4>🏆 Step 11: Winners</h4>
              <div style={{background: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fde047', lineHeight: '1.6'}}>
                <div style={{marginBottom: '8px', padding: '6px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '4px'}}>
                  <strong style={{color: '#ffd700'}}>🏆 SHOW FINAL LEADERBOARD</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY shows leaderboard on all screens.</span><br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Click this FIRST before announcing winners.</span>
                </div>
                <div style={{padding: '6px', background: 'rgba(148, 163, 184, 0.15)', borderRadius: '4px'}}>
                  <strong>🥉🥈🥇 ANNOUNCE BUTTONS</strong> = <span style={{color: '#22c55e', fontWeight: 'bold'}}>INSTANTLY shows winner spotlight.</span><br/>
                  <span style={{fontSize: '11px', color: '#94a3b8'}}>Click in order: 3rd → 2nd → 1st for dramatic reveals!</span>
                </div>
              </div>
              <div className="party-phase-buttons">
                <button className="phase-btn final-results" style={{background: '#ffd700', color: '#000', width: '100%', marginBottom: '10px'}} onClick={showFinalResults}>
                  🏆 Show Final Leaderboard (instant)
                </button>
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px'}}>
                <button className="phase-btn event" style={{background: '#cd7f32'}} onClick={() => sendShowEvent('winner-announcement', { place: 3 })}>
                  🥉 3rd Place
                </button>
                <button className="phase-btn event" style={{background: '#c0c0c0', color: '#000'}} onClick={() => sendShowEvent('winner-announcement', { place: 2 })}>
                  🥈 2nd Place
                </button>
                <button className="phase-btn event" style={{background: '#ffd700', color: '#000'}} onClick={() => sendShowEvent('winner-announcement', { place: 1 })}>
                  🥇 1st Place
                </button>
              </div>
            </div>

            {/* STEP 12: Award Ceremony */}
            <div className="party-section" style={{border: '2px solid #ffd700', background: 'linear-gradient(135deg, #2a2a1a 0%, #1a1a2e 100%)'}}>
              <h4>🎬 Step 12: Award Ceremony (Spectate Page)</h4>
              <div style={{marginBottom: '10px'}}>
                <button className="phase-btn" style={{background: '#ffd700', color: '#000', width: '100%', fontSize: '14px', padding: '10px'}} onClick={() => window.open('/spectate/ceremony', '_blank')}>
                  🏆 Open Ceremony Page ↗
                </button>
              </div>
              <div style={{background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)', borderRadius: '6px', padding: '10px', marginBottom: '12px', fontSize: '12px', color: '#fde047', lineHeight: '1.6'}}>
                <strong>Open /spectate/ceremony on projector.</strong><br/>
                <span style={{fontSize: '11px', color: '#94a3b8'}}>1. Start Ceremony (loads leaderboard) → 2. Click NEXT for each reveal.<br/>Order: Last Place (joke) → 10th → 9th → ... → 2nd → 1st (most dramatic).</span>
              </div>
              <div className="party-phase-buttons" style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                <button className="phase-btn" style={{background: '#ffd700', color: '#000', flex: 1}} onClick={async () => {
                  try {
                    const response = await adminApi.post('/api/admin/ceremony/start', { topCount: 10 });
                    if (response.data.success) {
                      setMessage({ text: `Ceremony started with ${response.data.ceremony.leaderboard.length} winners`, type: 'success' });
                    }
                  } catch (error) {
                    setMessage({ text: error.response?.data?.message || 'Error starting ceremony', type: 'error' });
                  }
                }}>
                  🎬 Start Ceremony
                </button>
                <button className="phase-btn" style={{background: '#ef4444', flex: 0.5}} onClick={async () => {
                  try {
                    await adminApi.post('/api/admin/ceremony/reset');
                    setMessage({ text: 'Ceremony reset', type: 'success' });
                  } catch (error) {
                    setMessage({ text: 'Error resetting ceremony', type: 'error' });
                  }
                }}>
                  Reset
                </button>
              </div>
              <button className="phase-btn" style={{background: 'linear-gradient(135deg, #ffd700, #ff8c00)', color: '#000', width: '100%', fontSize: '18px', padding: '15px', fontWeight: 'bold', letterSpacing: '2px'}} onClick={async () => {
                try {
                  const response = await adminApi.post('/api/admin/ceremony/next');
                  if (response.data.done) {
                    setMessage({ text: 'Ceremony complete! All winners revealed.', type: 'success' });
                  } else if (response.data.person) {
                    const p = response.data.person;
                    setMessage({ text: `Revealed: ${p.rank === 'LAST' ? 'LAST PLACE' : '#' + p.rank} - ${p.username}`, type: 'success' });
                  }
                } catch (error) {
                  setMessage({ text: error.response?.data?.message || 'Error', type: 'error' });
                }
              }}>
                NEXT PERSON →
              </button>
            </div>

            {/* Quick Events */}
            <div className="party-section">
              <h4>📡 Quick Visual Effects</h4>
              <div className="party-phase-buttons broadcast-events">
                <button className="phase-btn event" onClick={() => sendBroadcastEvent('market', { event: 'countdown', data: { seconds: 3 } })}>3-2-1</button>
                <button className="phase-btn event" onClick={() => sendBroadcastEvent('market', { event: 'opening-bell' })}>🔔 Bell</button>
                <button className="phase-btn event" onClick={() => sendBroadcastEvent('market', { event: 'major-move', data: { direction: 'up' } })}>📈 Up</button>
                <button className="phase-btn event" onClick={() => sendBroadcastEvent('market', { event: 'major-move', data: { direction: 'down' } })}>📉 Down</button>
                <button className="phase-btn event" onClick={() => sendBroadcastEvent('broadcast', { type: 'applause' })}>👏 Applause</button>
              </div>
            </div>

            {/* Section 10: Force Phase Transition (Emergency Override) */}
            <div className="party-section" style={{background: 'linear-gradient(135deg, #4a1a1a 0%, #2a1a1a 100%)', border: '2px solid #dc2626'}}>
              <h4>⚠️ Force Phase Transition (Emergency Override)</h4>
              <p style={{fontSize: '11px', color: '#f87171', marginBottom: '10px'}}>
                Use these to force jump to any phase. Skips normal flow - use only if needed.
              </p>

              {/* Restart Party Flow Button */}
              <div style={{marginBottom: '15px'}}>
                <button
                  className="phase-btn"
                  style={{
                    width: '100%',
                    fontSize: '16px',
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: '2px solid #fbbf24'
                  }}
                  onClick={restartPartyFlow}
                >
                  🔄 RESTART PARTY FLOW (Back to Beginning)
                </button>
                <p style={{fontSize: '10px', color: '#fcd34d', marginTop: '5px', textAlign: 'center'}}>
                  Resets phase to start. Does NOT delete game data (players, trades, etc).
                </p>
              </div>

              <div className="party-phase-buttons broadcast-events" style={{gap: '8px'}}>
                <button className="phase-btn event" style={{background: '#3b82f6'}} onClick={() => forcePhase('registration')}>
                  📝 Registration
                </button>
                <button className="phase-btn event" style={{background: '#8b5cf6'}} onClick={() => forcePhase('premarket')}>
                  📺 Premarket
                </button>
                <button className="phase-btn event" style={{background: '#a855f7'}} onClick={() => forcePhase('orientation')}>
                  🎬 Orientation
                </button>
                <button className="phase-btn event" style={{background: '#22c55e'}} onClick={() => forcePhase('trading', { tradingBlockNumber: 1 })}>
                  🌏 Asian
                </button>
                <button className="phase-btn event" style={{background: '#16a34a'}} onClick={() => forcePhase('trading', { tradingBlockNumber: 2 })}>
                  🇪🇺 European
                </button>
                <button className="phase-btn event" style={{background: '#15803d'}} onClick={() => forcePhase('trading', { tradingBlockNumber: 3 })}>
                  🇺🇸 Canadian
                </button>
                <button className="phase-btn event" style={{background: '#166534'}} onClick={() => forcePhase('trading', { tradingBlockNumber: 4 })}>
                  🔔 Closing Bell
                </button>
                <button className="phase-btn event" style={{background: '#c026d3'}} onClick={() => forcePhase('macro_game', { currentMacroGame: 'Portfolio Warfare' })}>
                  💼 Portfolio Warfare
                </button>
                <button className="phase-btn event" style={{background: '#db2777'}} onClick={() => forcePhase('macro_game', { currentMacroGame: 'Dead Cat Bounce' })}>
                  ⛳ Dead Cat Bounce
                </button>
                <button className="phase-btn event" style={{background: '#e11d48'}} onClick={() => forcePhase('macro_game', { currentMacroGame: 'A Brief Case' })}>
                  💰 A Brief Case
                </button>
                <button className="phase-btn event" style={{background: '#be123c'}} onClick={() => forcePhase('macro_game', { currentMacroGame: 'Liquidity Crisis' })}>
                  🌀 Liquidity Crisis
                </button>
                <button className="phase-btn event" style={{background: '#dc2626'}} onClick={() => forcePhase('ended')}>
                  🏁 Ended
                </button>
              </div>

              {/* Quick Countdown Triggers for Testing */}
              <div style={{marginTop: '15px', background: '#1e293b', padding: '12px', borderRadius: '8px'}}>
                <h5 style={{color: '#60a5fa', marginBottom: '10px'}}>⏱️ Quick Countdown Triggers (Testing)</h5>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'}}>
                  <div>
                    <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '4px'}}>Opening Bell</div>
                    <div style={{display: 'flex', gap: '4px'}}>
                      <button className="phase-btn event" style={{background: '#10b981', padding: '4px 8px', fontSize: '11px'}} onClick={() => startMarketCountdown(10)}>10s</button>
                      <button className="phase-btn event" style={{background: '#10b981', padding: '4px 8px', fontSize: '11px'}} onClick={() => startMarketCountdown(30)}>30s</button>
                      <button className="phase-btn event" style={{background: '#10b981', padding: '4px 8px', fontSize: '11px'}} onClick={() => startMarketCountdown(60)}>60s</button>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '4px'}}>Closing Bell</div>
                    <div style={{display: 'flex', gap: '4px'}}>
                      <button className="phase-btn event" style={{background: '#ef4444', padding: '4px 8px', fontSize: '11px'}} onClick={() => startCloseCountdown(10)}>10s</button>
                      <button className="phase-btn event" style={{background: '#ef4444', padding: '4px 8px', fontSize: '11px'}} onClick={() => startCloseCountdown(30)}>30s</button>
                      <button className="phase-btn event" style={{background: '#ef4444', padding: '4px 8px', fontSize: '11px'}} onClick={() => startCloseCountdown(60)}>60s</button>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '4px'}}>Trading Session</div>
                    <div style={{display: 'flex', gap: '4px'}}>
                      <button className="phase-btn event" style={{background: '#3b82f6', padding: '4px 8px', fontSize: '11px'}} onClick={() => startTradingBlockCountdown(60)}>1m</button>
                      <button className="phase-btn event" style={{background: '#3b82f6', padding: '4px 8px', fontSize: '11px'}} onClick={() => startTradingBlockCountdown(300)}>5m</button>
                      <button className="phase-btn event" style={{background: '#3b82f6', padding: '4px 8px', fontSize: '11px'}} onClick={() => startTradingBlockCountdown(1800)}>30m</button>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize: '10px', color: '#94a3b8', marginBottom: '4px'}}>Macro Game</div>
                    <div style={{display: 'flex', gap: '4px'}}>
                      <button className="phase-btn event" style={{background: '#8b5cf6', padding: '4px 8px', fontSize: '11px'}} onClick={() => quickMacroCountdown(10)}>10s</button>
                      <button className="phase-btn event" style={{background: '#8b5cf6', padding: '4px 8px', fontSize: '11px'}} onClick={() => quickMacroCountdown(30)}>30s</button>
                      <button className="phase-btn event" style={{background: '#8b5cf6', padding: '4px 8px', fontSize: '11px'}} onClick={() => quickMacroCountdown(60)}>60s</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Bar */}
          <div className="quick-actions">
            <button className="quick-action-btn bell" onClick={() => navigateToTab('bell')}>
              🔔 Market Bell
            </button>
            <button className="quick-action-btn qr" onClick={() => navigateToTab('qr-gen')}>
              📱 Generate QR
            </button>
            <button className="quick-action-btn game" onClick={() => navigateToTab('battleship')}>
              💼 Portfolio Warfare
            </button>
            <button className="quick-action-btn users" onClick={() => navigateToTab('users')}>
              👥 Manage Users
            </button>
          </div>

          {dashboardData ? (
            <>
              {/* Status Cards */}
              <div className="dashboard-grid">
                <div className="dashboard-card highlight">
                  <div className="card-icon">👥</div>
                  <div className="card-content">
                    <div className="big-number">{dashboardData.participantCount}</div>
                    <div className="card-label">Total Participants</div>
                  </div>
                </div>

                <div className={`dashboard-card highlight ${dashboardData.marketState.isOpen ? 'success' : 'warning'}`}>
                  <div className="card-icon">{dashboardData.marketState.isOpen ? '🟢' : '🔴'}</div>
                  <div className="card-content">
                    <div className="status-text">{dashboardData.marketState.isOpen ? 'OPEN' : 'CLOSED'}</div>
                    <div className="card-label">Market Status</div>
                    <small>Trading: {dashboardData.marketState.tradingEnabled ? '✅ Enabled' : '⏸️ Paused'}</small>
                  </div>
                </div>

                <div className="dashboard-card">
                  <div className="card-icon">💰</div>
                  <div className="card-content">
                    <div className="big-number">{dashboardData.recentTradeCount}</div>
                    <div className="card-label">Trades (Last 10min)</div>
                  </div>
                </div>

                <div className="dashboard-card">
                  <div className="card-icon">🎮</div>
                  <div className="card-content">
                    <div className="big-number">{dashboardData.minigameActivity}</div>
                    <div className="card-label">Minigame Plays (1hr)</div>
                  </div>
                </div>
              </div>

              {/* Detailed Stats */}
              <div className="stats-section">
                <div className="stat-panel">
                  <h3>💵 Balance Distribution</h3>
                  <div className="stat-grid">
                    <div className="stat-item">
                      <span className="stat-label">Highest</span>
                      <span className="stat-value positive">${dashboardData.marketActivity.maxBalance.toFixed(2)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Average</span>
                      <span className="stat-value">${dashboardData.marketActivity.avgBalance.toFixed(2)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Lowest</span>
                      <span className="stat-value negative">${dashboardData.marketActivity.minBalance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {dashboardData.minigameQueues && dashboardData.minigameQueues.length > 0 && (
                  <div className="stat-panel">
                    <h3>🎯 Active Minigame Queues</h3>
                    <div className="queue-list">
                      {dashboardData.minigameQueues.map(queue => (
                        <div key={queue.minigame_id} className="queue-item">
                          <span className="queue-name">{queue.minigame_id}</span>
                          <span className="queue-count">{queue.queue_count} waiting</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* System Info */}
              <div className="system-info">
                <h3>🔧 System Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Database</span>
                    <span className={`info-value ${systemHealth.database}`}>{systemHealth.database}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">WebSocket</span>
                    <span className={`info-value ${systemHealth.websocket}`}>{systemHealth.websocket}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Refresh</span>
                    <span className="info-value">{new Date(systemHealth.lastUpdate).toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading dashboard data...</p>
            </div>
          )}
        </div>
      )}

      {/* TRADES TAB */}
      {activeTab === 'trades' && (
        <div className="monitoring-tab">
          <h2>💰 Live Trading Activity</h2>
          <p className="realtime-note">📡 Real-time updates • Auto-refreshes every 3 seconds</p>

          {/* Trading Pressure Section */}
          <div className="pressure-section">
            <h3>📊 Trading Pressure</h3>
            <div className="actions">
              <button onClick={fetchTradingPressure} disabled={loading}>
                {loading ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>
            {loading ? (
              <div className="loading">Loading trading pressure data...</div>
            ) : (
              renderTradingPressure()
            )}
          </div>

          {/* Live Trades */}
          {renderTradingActivity()}
        </div>
      )}

      {/* MARKET TAB */}
      {activeTab === 'market' && (
        <div className="monitoring-tab">
          <h2>📉 Market Statistics</h2>
          <div className="market-settings-panel">
            <h3>Market Engine Settings</h3>
            <form onSubmit={updateMarketSettings}>
              <div className="form-row">
                <label>
                  Trading Pressure Impact Factor (0-1):
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={marketSettings.pressureImpactFactor}
                    onChange={(e) => setMarketSettings({
                      ...marketSettings,
                      pressureImpactFactor: parseFloat(e.target.value)
                    })}
                    required
                  />
                </label>
                <div className="setting-help">How much trading affects prices. Higher values = more impact.</div>
              </div>

              <div className="form-row">
                <label>
                  Price Update Interval (ms):
                  <input
                    type="number"
                    min="1000"
                    max="30000"
                    step="1000"
                    value={marketSettings.updateInterval}
                    onChange={(e) => setMarketSettings({
                      ...marketSettings,
                      updateInterval: parseInt(e.target.value)
                    })}
                    required
                  />
                </label>
                <div className="setting-help">How often prices update. Lower = more frequent updates.</div>
              </div>

              <button type="submit" className="update-settings-btn" disabled={isUpdatingSettings}>
                {isUpdatingSettings ? 'Updating...' : 'Update Settings'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* REGISTER TAB */}
      {activeTab === 'register' && (
        <div className="register-tab-container">
          <div className="register-tab-grid">
            <div className="register-section">
              <AdminGuestRegistration />
            </div>
            <div className="recent-section">
              <AdminRecentRegistrations />
            </div>
          </div>
        </div>
      )}

      {/* BELL TAB */}
      {activeTab === 'bell' && (
        <div className="controls-tab">
          <h2>🔔 Market Opening Bell Control</h2>
          <div className="bell-control">
            <div className="market-status">
              <h3>Current Market Status:</h3>
              <div className={`status-display ${marketState.isOpen ? 'open' : 'closed'}`}>
                {marketState.isOpen ? '🟢 MARKET IS OPEN' : '🔴 MARKET IS CLOSED'}
              </div>
              {marketState.openedAt && (
                <small>Opened at: {new Date(marketState.openedAt).toLocaleString()}</small>
              )}
              <div className="trading-status">
                Trading: {marketState.tradingEnabled ? '✅ Enabled' : '⏸️ Paused'}
              </div>
            </div>
            
            <div className="bell-buttons">
              <button 
                className="bell-button open-button"
                onClick={() => toggleMarket('open')}
                disabled={marketState.isOpen}
              >
                🔔 RING OPENING BELL
              </button>
              
              <button 
                className="bell-button close-button"
                onClick={() => toggleMarket('close')}
                disabled={!marketState.isOpen}
              >
                🔔 RING CLOSING BELL
              </button>
              
              {marketState.isOpen && (
                <div className="trading-controls">
                  {marketState.tradingEnabled ? (
                    <button 
                      className="bell-button pause-button"
                      onClick={pauseTrading}
                    >
                      ⏸️ PAUSE TRADING
                    </button>
                  ) : (
                    <button 
                      className="bell-button resume-button"
                      onClick={resumeTrading}
                    >
                      ▶️ RESUME TRADING
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="bell-info">
              <h4>What happens when you use these controls:</h4>
              <ul>
                <li><strong>Opening Bell:</strong> Enables trading for all users, broadcasts announcement to all players</li>
                <li><strong>Closing Bell:</strong> Disables trading, freezes positions for final calculation</li>
                <li><strong>Pause Trading:</strong> Temporarily disables trading while keeping market open</li>
                <li><strong>Resume Trading:</strong> Re-enables trading after pause or big game</li>
              </ul>
              <div className="broadcast-info">
                <p><strong>📡 Live Updates:</strong> All market state changes are broadcast in real-time to all connected users and displayed here.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR GEN TAB */}
      {activeTab === 'qr-gen' && (
        <div className="controls-tab qr-generator-tab">
          <h2>📱 Real-time QR Code Generator</h2>
          <div className="qr-generator">
            <div className="qr-form">
              <h3>Generate Balance Adjustment QR Code</h3>
              
              <div className="form-group">
                <label>Type:</label>
                <select 
                  value={qrGenerator.type}
                  onChange={(e) => setQrGenerator(prev => ({...prev, type: e.target.value}))}
                >
                  <option value="bonus">Bonus (Add Money)</option>
                  <option value="penalty">Penalty (Subtract Money)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Amount ($):</label>
                <input
                  type="number"
                  step="0.01"
                  value={qrGenerator.amount}
                  onChange={(e) => setQrGenerator(prev => ({...prev, amount: e.target.value}))}
                  placeholder="Enter amount"
                />
              </div>
              
              <div className="form-group">
                <label>Reason:</label>
                <input
                  type="text"
                  value={qrGenerator.reason}
                  onChange={(e) => setQrGenerator(prev => ({...prev, reason: e.target.value}))}
                  placeholder="e.g., 'Big game winner', 'Rule violation penalty'"
                />
              </div>
              
              <button 
                className="generate-qr-button"
                onClick={generateQRCode}
              >
                Generate QR Code
              </button>
            </div>
            
            {qrGenerator.generatedQR && (
              <div className="generated-qr">
                <h3>Generated QR Code:</h3>
                <div className="qr-display">
                  <div className="qr-code-text">
                    {qrGenerator.generatedQR.qrCode}
                  </div>
                  <div className="qr-details">
                    <p><strong>Type:</strong> {qrGenerator.generatedQR.type}</p>
                    <p><strong>Amount:</strong> ${Math.abs(qrGenerator.generatedQR.amount)}</p>
                    <p><strong>Reason:</strong> {qrGenerator.generatedQR.reason}</p>
                  </div>
                  <div className="qr-instructions">
                    <p>📱 Display this QR code on your phone for players to scan</p>
                    <p>⚠️ This code can only be used once</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GAMES TAB */}
      {activeTab === 'games' && (
        <div className="controls-tab big-games-tab">
          <h2>🎮 Big Team Games Management</h2>
          
          <div className="big-games-control">
            <div className="game-announcer">
              <h3>Announce Big Game</h3>
              
              <div className="form-group">
                <label>Game Name:</label>
                <select
                  value={bigGameData.gameName}
                  onChange={(e) => setBigGameData(prev => ({...prev, gameName: e.target.value}))}
                >
                  <option value="">Select a game...</option>
                  <option value="Portfolio Warfare">💼 Portfolio Warfare</option>
                  <option value="Dead Cat Bounce">⛳ Dead Cat Bounce</option>
                  <option value="A Brief Case">💰 A Brief Case</option>
                  <option value="Liquidity Crisis">🌀 Liquidity Crisis</option>
                  <option value="Custom Game">Custom Game</option>
                </select>
              </div>
              
              {bigGameData.gameName === 'Custom Game' && (
                <div className="form-group">
                  <label>Custom Game Name:</label>
                  <input
                    type="text"
                    value={bigGameData.customName || ''}
                    onChange={(e) => setBigGameData(prev => ({...prev, customName: e.target.value, gameName: e.target.value}))}
                    placeholder="Enter custom game name"
                  />
                </div>
              )}
              
              <div className="form-group">
                <label>Announcement Message:</label>
                <textarea
                  value={bigGameData.announcement}
                  onChange={(e) => setBigGameData(prev => ({...prev, announcement: e.target.value}))}
                  placeholder="e.g., 'Everyone gather at the main stage! The next macro game is about to begin. Teams will be assigned at random.'"
                  rows={4}
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={bigGameData.pauseTrading}
                    onChange={(e) => setBigGameData(prev => ({...prev, pauseTrading: e.target.checked}))}
                  />
                  Pause trading during this game
                </label>
              </div>
              
              <button 
                className="announce-game-button"
                onClick={announceBigGame}
              >
                📢 Announce Big Game
              </button>
            </div>
            
            <div className="trading-control">
              <h3>Trading Control</h3>
              <div className="trading-status">
                Current Status: {marketState.tradingEnabled ? '✅ Trading Active' : '⏸️ Trading Paused'}
              </div>
              
              {!marketState.tradingEnabled && (
                <button 
                  className="resume-trading-button"
                  onClick={resumeTrading}
                >
                  📈 Resume Trading
                </button>
              )}
            </div>
            
            <div className="big-game-instructions">
              <h3>Big Game Instructions:</h3>
              <ol>
                <li><strong>Announce the game</strong> - This broadcasts to all players</li>
                <li><strong>Form teams manually</strong> - Organize players into teams at the venue</li>
                <li><strong>Run the physical game</strong> - Conduct the game activity</li>
                <li><strong>Present QR codes</strong> - Use pre-printed winner/loser QR codes</li>
                <li><strong>Resume trading</strong> - Re-enable trading when game is complete</li>
              </ol>
              
              <div className="qr-reminder">
                <h4>📱 QR Code Distribution:</h4>
                <p>Use the <strong>QR Generator</strong> tab to create team-specific bonus/penalty codes, or use pre-printed codes with appropriate values for your big games.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Macro Game Controls - merged into games tab below */}
      {activeTab === 'games-macro' && (
        <div className="controls-tab macro-controls-tab">
          <h2>🎯 Macro Game Market Controls</h2>

          <div className="macro-controls-explainer">
            <p>Use these controls to pause trading and/or market events during macro games. This ensures players focus on the team activity without market distractions.</p>
          </div>

          <div className="control-status-grid">
            {/* Current State Display */}
            <div className="control-status-card">
              <h3>📊 Current State</h3>
              <div className="status-indicators">
                <div className={`status-item ${macroControlState.eventsPaused ? 'paused' : 'active'}`}>
                  <span className="status-icon">{macroControlState.eventsPaused ? '⏸️' : '✅'}</span>
                  <span className="status-label">Market Events:</span>
                  <span className="status-value">{macroControlState.eventsPaused ? 'PAUSED' : 'ACTIVE'}</span>
                </div>
                <div className={`status-item ${macroControlState.tradingPaused ? 'paused' : 'active'}`}>
                  <span className="status-icon">{macroControlState.tradingPaused ? '⏸️' : '✅'}</span>
                  <span className="status-label">Trading:</span>
                  <span className="status-value">{macroControlState.tradingPaused ? 'PAUSED' : 'ACTIVE'}</span>
                </div>
                <div className={`status-item ${macroControlState.marketPaused ? 'paused' : 'active'}`}>
                  <span className="status-icon">{macroControlState.marketPaused ? '⏸️' : '✅'}</span>
                  <span className="status-label">Entire Market:</span>
                  <span className="status-value">{macroControlState.marketPaused ? 'PAUSED' : 'ACTIVE'}</span>
                </div>
              </div>
            </div>

            {/* Events Control */}
            <div className="control-card">
              <h3>🎲 Market Events Control</h3>
              <p>Pause random market events (crashes, bull runs, mergers) while keeping trading active.</p>
              <div className="control-buttons">
                {macroControlState.eventsPaused ? (
                  <button
                    className="control-btn resume-btn"
                    onClick={resumeEvents}
                    disabled={macroControlState.marketPaused}
                  >
                    ▶️ Resume Events
                  </button>
                ) : (
                  <button
                    className="control-btn pause-btn"
                    onClick={pauseEvents}
                    disabled={macroControlState.marketPaused}
                  >
                    ⏸️ Pause Events
                  </button>
                )}
              </div>
              {macroControlState.marketPaused && (
                <small className="disabled-note">⚠️ Cannot control events while entire market is paused</small>
              )}
            </div>

            {/* Trading Control */}
            <div className="control-card">
              <h3>💰 Trading Control</h3>
              <p>Pause all trading while prices continue to update and events can occur.</p>
              <div className="control-buttons">
                {macroControlState.tradingPaused ? (
                  <button
                    className="control-btn resume-btn"
                    onClick={resumeTrading}
                    disabled={macroControlState.marketPaused}
                  >
                    ▶️ Resume Trading
                  </button>
                ) : (
                  <button
                    className="control-btn pause-btn"
                    onClick={pauseTrading}
                    disabled={macroControlState.marketPaused}
                  >
                    ⏸️ Pause Trading
                  </button>
                )}
              </div>
              {macroControlState.marketPaused && (
                <small className="disabled-note">⚠️ Cannot control trading while entire market is paused</small>
              )}
            </div>

            {/* Price Updates Control */}
            <div className="control-card">
              <h3>📊 Price Updates</h3>
              <p>Freeze all stock prices during macro games.</p>
              <div className="control-buttons">
                {macroControlState.pricesPaused ? (
                  <button
                    className="control-btn resume-btn"
                    onClick={resumePrices}
                    disabled={macroControlState.marketPaused}
                  >
                    ▶️ Resume Prices
                  </button>
                ) : (
                  <button
                    className="control-btn pause-btn"
                    onClick={pausePrices}
                    disabled={macroControlState.marketPaused}
                  >
                    ⏸️ Pause Prices
                  </button>
                )}
              </div>
              {macroControlState.marketPaused && (
                <small className="disabled-note">⚠️ Cannot control prices while entire market is paused</small>
              )}
            </div>

            {/* Full Market Control */}
            <div className="control-card market-wide">
              <h3>🎯 Full Market Control</h3>
              <p>Pause EVERYTHING (trading + events) for major macro games.</p>
              <div className="control-buttons">
                {macroControlState.marketPaused ? (
                  <button
                    className="control-btn resume-btn large"
                    onClick={resumeMarket}
                  >
                    ▶️ Resume Entire Market
                  </button>
                ) : (
                  <button
                    className="control-btn pause-btn large"
                    onClick={pauseMarket}
                  >
                    ⏸️ Pause Entire Market
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="macro-game-workflow">
            <h3>📋 Typical Macro Game Workflow:</h3>
            <div className="workflow-steps">
              <div className="workflow-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Before Game Starts:</strong>
                  <p>Pause market events (or entire market) to prevent distractions</p>
                  <button className="workflow-quick-btn" onClick={pauseEvents}>⏸️ Pause Events</button>
                </div>
              </div>
              <div className="workflow-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>During Game:</strong>
                  <p>Run your macro game. Market stays paused.</p>
                </div>
              </div>
              <div className="workflow-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>After Game Ends:</strong>
                  <p>Distribute prizes via QR codes, then resume normal market</p>
                  <button className="workflow-quick-btn" onClick={resumeEvents}>▶️ Resume Events</button>
                </div>
              </div>
            </div>
          </div>

          <div className="control-info-box">
            <h4>ℹ️ What Each Control Does:</h4>
            <ul>
              <li><strong>Pause Events:</strong> Stops random market crashes, bull runs, and other events. Prices and trading still work normally.</li>
              <li><strong>Pause Trading:</strong> Prevents all buy/sell actions. Players can still view the market and prices still update.</li>
              <li><strong>Pause Prices:</strong> Freezes all stock prices. Trading pressure still accumulates but prices don't change until resumed.</li>
              <li><strong>Pause Market:</strong> Stops everything - no trading, no events, no price changes. Total freeze for macro games.</li>
            </ul>
          </div>
        </div>
      )}

      {/* EVENTS TAB */}
      {activeTab === 'events' && (
        <CustomMarketEvents />
      )}

      {/* CONCENTRATION WATCH TAB */}
      {activeTab === 'concentration' && (
        <AdminConcentrationWatch />
      )}

      {/* LED TAB */}
      {activeTab === 'led' && (
        <AdminLEDControl />
      )}

      {/* BATTLESHIP TAB */}
      {activeTab === 'battleship' && (
        <div>
          <div style={{ marginBottom: '15px', padding: '10px', background: '#1a1a2e', borderRadius: '8px' }}>
            <a
              href="/admin/battleship-fullscreen"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#4fc3f7',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#2a2a4e',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              Open in New Tab &rarr;
            </a>
            <a
              href="/admin/battleship-monopoly"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#00ff88',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#1a3c1a',
                borderRadius: '4px',
                fontSize: '14px',
                marginLeft: '10px'
              }}
            >
              Komok-opoly Board &rarr;
            </a>
            <a
              href="/spectate/tv"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#ffcc00',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: '#3c2a1a',
                borderRadius: '4px',
                fontSize: '14px',
                marginLeft: '10px'
              }}
            >
              TV View (Split Screen) &rarr;
            </a>
            <span style={{ marginLeft: '15px', color: '#888', fontSize: '13px' }}>
              (Dedicated page won't reset when switching admin tabs)
            </span>
          </div>
          <AdminBattleship />
        </div>
      )}

      {/* TESTER TAB */}
      {activeTab === 'tester' && (
        <AdminEventTester />
      )}

      {/* USERS TAB */}
      {activeTab === 'users' && (
        <AdminUserManagementTab />
      )}

      {/* QR CODES TAB */}
      {activeTab === 'qr-codes' && (
        <AdminQRCodeTab />
      )}

      {/* BRIEFCASE BLITZ TAB */}
      {activeTab === 'briefcase' && (
        <AdminBriefcaseBlitz />
      )}

      {/* MINIGOLF GAUNTLET TAB */}
      {activeTab === 'minigolf' && (
        <AdminMinigolf />
      )}

      {/* MONEY TUNNEL TAB */}
      {activeTab === 'tunnel' && (
        <AdminMoneyTunnel />
      )}

      {/* TEAMS TAB */}
      {activeTab === 'teams' && (
        <AdminTeams />
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
        <AdminOverrides />
      )}

      {/* OBSERVABILITY TAB */}
      {activeTab === 'observability' && (
        <AdminObservability />
      )}

      {/* RESET TAB */}
      {activeTab === 'ceos' && (
        <CeoManager />
      )}

      {activeTab === 'videos' && (
        <div style={{ padding: '20px', background: '#0d0d1a', borderRadius: '8px' }}>
          <h2 style={{ color: '#00ff65', marginBottom: '10px' }}>Game Videos & Resources</h2>
          <p style={{ color: '#888', marginBottom: '20px', fontSize: '13px' }}>
            Quick reference for all game videos and presentations. Click to open.
          </p>
          {[
            {
              section: 'GENERAL',
              items: [
                { label: 'Registration Video', type: 'video', url: '/videos/registration.mp4', desc: 'Onboarding video shown during registration' },
                { label: 'Orientation Video', type: 'video', url: '/videos/orientation_video.mp4', desc: 'Training protocol / orientation' },
              ]
            },
            {
              section: 'MACRO GAME 1 — PORTFOLIO WARFARE',
              items: [
                { label: 'Portfolio Warfare Video', type: 'video', url: '/videos/portfolio-warfare.mp4', desc: 'Intro video for Portfolio Warfare' },
                { label: 'Portfolio Warfare Brief', type: 'link', url: '/portfolio-warfare-brief.html', desc: 'Mission brief with rules (public page)' },
              ]
            },
            {
              section: 'MACRO GAME 2 — DEAD CAT BOUNCE',
              items: [
                { label: 'Dead Cat Bounce Video', type: 'video', url: '/videos/dead-cat-bounce.mp4', desc: 'Intro video for Dead Cat Bounce' },
                { label: 'Dead Cat Bounce Slides (Google)', type: 'external', url: 'https://docs.google.com/presentation/d/1DjxO-Q2uz6XwrGep4fGbqTUciPusJRdnqa3FEkYVdpg/edit?slide=id.g3c56e6ab56b_2_150#slide=id.g3c56e6ab56b_2_150', desc: 'PPT slides for the golf game' },
              ]
            },
            {
              section: 'MACRO GAME 3 — A BRIEF CASE',
              items: [
                { label: 'A Brief Case Video', type: 'video', url: '/videos/a_brief_case_video.mp4', desc: 'Intro video for A Brief Case' },
              ]
            },
            {
              section: 'MACRO GAME 4 — LIQUIDITY CRISIS',
              items: [
                { label: 'Liquidity Crisis Video', type: 'video', url: '/videos/liquidity-crisis.mp4', desc: 'Intro video for Liquidity Crisis' },
              ]
            },
          ].map(({ section, items }) => (
            <div key={section} style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#ffd700', fontSize: '14px', letterSpacing: '2px', marginBottom: '10px', borderBottom: '1px solid rgba(255,215,0,0.3)', paddingBottom: '6px' }}>{section}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map(({ label, type, url, desc }) => (
                  <div key={url} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <button
                      onClick={() => window.open(type === 'external' ? url : url, '_blank')}
                      style={{ background: type === 'video' ? 'rgba(0,255,101,0.1)' : type === 'external' ? 'rgba(255,165,0,0.1)' : 'rgba(0,188,212,0.1)', border: `1px solid ${type === 'external' ? 'rgba(255,165,0,0.5)' : type === 'link' ? 'rgba(0,188,212,0.5)' : 'rgba(0,255,101,0.5)'}`, color: type === 'external' ? '#ffa500' : type === 'link' ? '#00bcd4' : '#00ff65', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {type === 'video' ? '▶ Play' : type === 'external' ? '↗ Open' : '→ View'}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
                        {label}
                        <span style={{ marginLeft: '8px', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', background: type === 'video' ? 'rgba(0,255,101,0.15)' : type === 'external' ? 'rgba(255,165,0,0.15)' : 'rgba(0,188,212,0.15)', color: type === 'video' ? '#00ff65' : type === 'external' ? '#ffa500' : '#00bcd4' }}>
                          {type === 'video' ? 'MP4' : type === 'external' ? 'EXTERNAL' : 'PAGE'}
                        </span>
                      </div>
                      <div style={{ color: '#aaa', fontSize: '12px', marginTop: '2px' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'embeds' && (
        <div style={{ padding: '20px', background: '#0d0d1a', borderRadius: '8px' }}>
          <h2 style={{ color: '#00ff65', marginBottom: '20px' }}>Embed Links</h2>
          <p style={{ color: '#888', marginBottom: '20px', fontSize: '14px' }}>
            Click any link to open in a new tab. All embeds are public (no auth required) and update live via WebSocket.
          </p>
          {[
            {
              section: 'DASHBOARDS',
              links: [
                { path: '/embed/dashboard', label: 'Dashboard', desc: 'Market heatmap (75%) + Leaderboard (25%)' },
                { path: '/embed/dashboard?chart=false', label: 'Dashboard (no charts)', desc: 'Compact market grid + leaderboard' },
                { path: '/embed/leaderboard-logo', label: 'Leaderboard + Logo', desc: 'Leaderboard (50%) + green Komok logo (50%)' },
                { path: '/embed/home', label: 'Home (rotating)', desc: 'Switches between Monitor, Logo, Broadcast, Dashboards' },
                { path: '/monitor/index.html', label: 'Monitor Dashboard', desc: 'Full monitor dashboard — stocks, leaderboard, trades, events' },
                { path: '/embed/shuffle', label: 'Shuffle (random)', desc: 'Random embed every 2 min — set and forget for screens' },
                { path: '/embed/multi', label: 'Multi-Screen', desc: 'Random multi-panel layouts (2-4 embeds) — rotates every 2 min' },
                { path: '/embed/full-market', label: 'Full Market', desc: 'All stocks heatmap with sparklines' },
                { path: '/embed/full-market-compact', label: 'Full Market (compact)', desc: 'All stocks heatmap, no sparklines' },
                { path: '/embed/full-market-box', label: 'Full Market (box)', desc: 'Box-style market grid with sparklines' },
                { path: '/embed/market', label: 'Market Grid', desc: 'Small market overview (2x3 grid)' },
              ]
            },
            {
              section: 'LEADERBOARD',
              links: [
                { path: '/embed/leaderboard', label: 'Leaderboard', desc: 'Top 10 traders with PnL' },
                { path: '/embed/leaderboard?limit=25', label: 'Leaderboard (25)', desc: 'Top 25 traders' },
                { path: '/embed/leaderboard?limit=50&style=minimal', label: 'Leaderboard (50, minimal)', desc: 'Top 50, minimal style' },
              ]
            },
            {
              section: 'STOCK DETAIL',
              links: [
                { path: '/embed/detail/KOM', label: 'Stock Detail (KOM)', desc: 'Full stock page: CEO, chart, price, stats' },
                { path: '/embed/detail/KOM?sections=chart,price', label: 'Chart + Price only', desc: 'Just chart and price section' },
                { path: '/embed/detail/KOM?sections=ceo,price', label: 'CEO + Price only', desc: 'CEO photo and price' },
                { path: '/embed/detail/KOM?ceo=false&stats=false', label: 'No CEO, no stats', desc: 'Chart with company header and price' },
              ]
            },
            {
              section: 'CHARTS',
              links: [
                { path: '/embed/stock/KOM', label: 'Stock Chart (KOM)', desc: 'Area chart with volume' },
                { path: '/embed/stock/KOM?type=line', label: 'Line Chart (KOM)', desc: 'Line chart variant' },
                { path: '/embed/stocks/KOM,BAD,OSK', label: 'Multi-Stock Chart', desc: 'Compare multiple stocks' },
                { path: '/embed/stocks/KOM,BAD,OSK?normalized=true', label: 'Multi-Stock (normalized)', desc: 'Compare as % change' },
                { path: '/embed/random-stocks', label: 'Random Stocks', desc: 'Random selection, rotates every 60s' },
              ]
            },
            {
              section: 'TICKERS & PRICES',
              links: [
                { path: '/embed/ticker', label: 'Ticker Tape', desc: 'Scrolling stock ticker' },
                { path: '/embed/ticker?speed=fast', label: 'Ticker Tape (fast)', desc: 'Fast scrolling ticker' },
                { path: '/embed/bottom-ticker', label: 'Bottom Ticker', desc: 'Bottom bar with events + stocks' },
                { path: '/embed/price/KOM', label: 'Price Badge (KOM)', desc: 'Single stock price display' },
                { path: '/embed/events-ticker', label: 'Events Ticker', desc: 'Live market events feed' },
              ]
            },
            {
              section: 'RANDOM STOCK DETAIL (rotates every 60s)',
              links: [
                { path: '/embed/random-detail', label: 'Random Detail (full)', desc: 'Full stock detail: CEO, chart, price, stats — rotates' },
                { path: '/embed/random-detail?variant=chart', label: 'Random Detail (chart)', desc: 'Ticker + chart + price — rotates' },
                { path: '/embed/random-detail?variant=ceo', label: 'Random Detail (CEO)', desc: 'Ticker + CEO photo + price — rotates' },
                { path: '/embed/random-detail?variant=price', label: 'Random Detail (price)', desc: 'Ticker + price + stats — rotates' },
                { path: '/embed/random-detail?rotate=30000', label: 'Random Detail (30s)', desc: 'Full detail, faster rotation' },
              ]
            },
            {
              section: 'TABLES (live updating)',
              links: [
                { path: '/embed/trades-table', label: 'Trades Table', desc: 'All trades in a live-updating table' },
                { path: '/embed/trades-table?limit=50', label: 'Trades Table (50)', desc: 'Last 50 trades' },
                { path: '/embed/events-table', label: 'Events Table', desc: 'All market events in a live-updating table' },
                { path: '/embed/events-table?limit=50', label: 'Events Table (50)', desc: 'Last 50 events' },
              ]
            },
            {
              section: 'MACRO GAMES (spectate)',
              links: [
                { path: '/spectate/battleship', label: 'Portfolio Warfare', desc: 'Battleship spectator view' },
                { path: '/spectate/deadcat', label: 'Dead Cat Bounce', desc: 'Minigolf spectator view' },
                { path: '/spectate/briefcase', label: 'A Brief Case', desc: 'Briefcase spectator view' },
                { path: '/spectate/tv', label: 'Battleship TV', desc: 'Battleship board TV display' },
                { path: '/spectate/ceremony', label: 'Award Ceremony', desc: 'End-of-game ceremony reveal' },
                { path: '/spectate/ceremony?example', label: 'Ceremony (demo)', desc: 'Demo mode with fake traders' },
              ]
            },
            {
              section: 'OTHER',
              links: [
                { path: '/embed/countdown', label: 'Countdown', desc: 'Game countdown timer' },
                { path: '/embed/trades', label: 'Recent Trades (feed)', desc: 'Live trade feed (card style)' },
                { path: '/embed/portfolio', label: 'Portfolio', desc: 'User portfolio display' },
                { path: '/embed/briefcase', label: 'Briefcase Leaderboard', desc: 'Brief Case game leaderboard' },
                { path: '/embed/video', label: 'Video Player', desc: 'Phase-based video playback' },
                { path: '/embed/content/registration', label: 'Content (registration)', desc: 'Phase content: video/images/iframe' },
              ]
            },
          ].map(({ section, links }) => (
            <div key={section} style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#ffd700', fontSize: '14px', letterSpacing: '2px', marginBottom: '10px', borderBottom: '1px solid rgba(255,215,0,0.3)', paddingBottom: '6px' }}>{section}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {links.map(({ path, label, desc }) => (
                  <div key={path} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <button
                      onClick={() => window.open(path, '_blank')}
                      style={{ background: 'rgba(0,255,101,0.1)', border: '1px solid rgba(0,255,101,0.5)', color: '#00ff65', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Open
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{label}</div>
                      <div style={{ color: '#aaa', fontSize: '12px', marginTop: '2px' }}>{desc}</div>
                    </div>
                    <code
                      style={{ color: '#00bcd4', fontSize: '11px', cursor: 'pointer', flexShrink: 0, maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={`Click to copy: ${path}`}
                      onClick={() => { navigator.clipboard.writeText(window.location.origin + path); }}
                    >
                      {path}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(0,188,212,0.08)', borderRadius: '8px', border: '1px solid rgba(0,188,212,0.2)' }}>
            <h3 style={{ color: '#00bcd4', fontSize: '13px', marginBottom: '8px' }}>QUERY PARAMS</h3>
            <div style={{ color: '#bbb', fontSize: '12px', lineHeight: '1.8' }}>
              <div><code style={{ color: '#00bcd4' }}>bg</code> — Background color (hex or "transparent")</div>
              <div><code style={{ color: '#00bcd4' }}>accent</code> — Accent color for highlights</div>
              <div><code style={{ color: '#00bcd4' }}>chart</code> — Show/hide charts (true/false)</div>
              <div><code style={{ color: '#00bcd4' }}>refresh</code> — Polling interval in ms (default varies)</div>
              <div><code style={{ color: '#00bcd4' }}>limit</code> — Max items (leaderboard)</div>
              <div><code style={{ color: '#00bcd4' }}>layout</code> — Layout variant (vertical/horizontal/compact)</div>
              <div><code style={{ color: '#00bcd4' }}>sections</code> — Comma-separated sections for stock detail (company,ceo,chart,price,stats,desc)</div>
              <div style={{ marginTop: '6px', color: '#999' }}>Click any <code style={{ color: '#00bcd4' }}>path</code> to copy the full URL to clipboard.</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reset' && (
        <div className="danger-tab reset-panel">
          <h2>Game Reset Options</h2>
          
          <div className="warning-box">
            <h3>⚠️ WARNING: DESTRUCTIVE ACTIONS ⚠️</h3>
            <p>The actions on this page will reset game data and cannot be undone.</p>
            <p>Make sure you understand what each option does before proceeding.</p>
          </div>
          
          <div className="admin-key-section">
            <label htmlFor="adminKeyInput">Admin Key:</label>
            <input
              type="password"
              id="adminKeyInput"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Enter admin key"
              required
            />
            <div className="field-help">Required for all reset operations</div>
          </div>
          
          {resetResult.message && (
            <div className={`reset-result ${resetResult.type}`}>
              {resetResult.message}
            </div>
          )}
          
          <div className="reset-options">
            <div className="reset-option soft-reset">
              <h3>Soft Reset</h3>
              <p>Resets stock prices to base values and user balances to initial values.</p>
              <p>Keeps user accounts and QR code usage history.</p>
              <button 
                className="reset-btn soft" 
                onClick={() => performReset('soft')}
                disabled={isResetting}
              >
                {isResetting ? 'Processing...' : 'Perform Soft Reset'}
              </button>
            </div>
            
            <div className="reset-option hard-reset">
              <h3>Hard Reset</h3>
              <p>Deletes all game data including user accounts and transaction history.</p>
              <p>Marks all QR codes as unused so they can be used again.</p>
              <button 
                className="reset-btn hard" 
                onClick={() => performReset('hard')}
                disabled={isResetting}
              >
                {isResetting ? 'Processing...' : 'Perform Hard Reset'}
              </button>
            </div>
            
            <div className="reset-option complete-reset">
              <h3>Complete Reset</h3>
              <p>Deletes EVERYTHING including all QR codes.</p>
              <p>Regenerates all QR codes from scratch (registration, bonus, and minigame codes).</p>
              <p><strong>Note:</strong> This process will take several minutes to complete.</p>
              <button 
                className="reset-btn complete" 
                onClick={() => performReset('complete')}
                disabled={isResetting}
              >
                {isResetting ? 'Processing...' : 'Perform Complete Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default AdminPanel;